// System Imports
import React, {
    useState,
    useRef,
    useCallback,
    useContext,
    useMemo,
    useEffect,
    forwardRef,
    useImperativeHandle
} from 'react';
import { useNavigate } from 'react-router-dom';

// Firebase
import { db, realtime } from 'config/firebase';
import { ref as storageRef, set as storageSet } from 'firebase/database';
import {
    collection,
    query,
    getDocs,
    where,
    doc,
    updateDoc,
    setDoc,
    collectionGroup,
    addDoc,
    serverTimestamp,
    onSnapshot,
    getDoc,
    writeBatch,
} from 'firebase/firestore';

// AG-Grid
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { LicenseManager } from  'ag-grid-enterprise'    


// Styling
import { Card, Col, Row, Form, Badge, Button, Image, Modal, Table } from 'react-bootstrap';
import 'components/pages/newProducts/css/advancedProducts.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SoftBadge from 'components/common/SoftBadge';
import { toast } from 'react-toastify';
import AdvanceTable from 'components/merlin/advance-table/AdvanceTable';
import AdvanceTableWrapper from 'components/merlin/advance-table/AdvanceTableWrapper';
import AdvanceTablePagination from 'components/merlin/advance-table/AdvanceTablePagination';

// Lottie Animations
import Lottie from "lottie-react";
import dotsAnimation from 'assets/lottie/dots.json';

// Page Components
import { ProductSearchBar } from 'components/pages/newProducts/components/ProductSearchBar';
import { TabContainer } from 'components/pages/newProducts/components/TabContainer';
import { ServerPaginationControls } from 'components/pages/newProducts/components/ServerPaginationControls';
import { MenuBar } from 'components/pages/newProducts/components/MenuBar';
import { HeaderBar } from 'components/pages/newProducts/components/HeaderBar';
import { BulkActionToolbar } from 'components/pages/newProducts/components/BulkActionToolbar';
import { FilterPresetBar, PRESET_API_PARAMS } from 'components/pages/newProducts/components/FilterPresetBar';
import { BatchScopePanel } from 'components/pages/newProducts/components/BatchScopePanel';
import { StatusBadgeRenderer, computeProductStatus, CategoryBadgeRenderer, SelectCellEditor } from 'components/pages/newProducts/components/StatusBadgeRenderer';

// Modals 
import { ManageShippingTemplatesModal } from 'components/pages/newProducts/modals/ManageShippingTemplatesModal';
import { ManagePricingTemplatesModal } from 'components/pages/newProducts/modals/ManagePricingTemplatesModal';
import { AsinDetailModal } from 'components/pages/newProducts/modals/AsinDetailModal';
import { ConfirmationModal } from 'components/pages/newProducts/modals/ConfirmationModal';
import { AssignTemplateModal } from 'components/pages/newProducts/modals/AssignTemplateModal';
import { BulkTagsModal } from 'components/pages/newProducts/modals/bulkTags';
import { NewListingModal } from 'components/pages/newProducts/modals/newListingModal';
import { OpenWorkspaceModal } from 'components/pages/newProducts/modals/OpenWorkspaceModal';
import { ListingReadinessOffcanvas } from 'components/pages/newProducts/modals/ListingReadinessOffcanvas';
import { EnrichmentModeModal } from 'components/pages/newProducts/modals/EnrichmentModeModal';
import { SelectAllActionModal } from 'components/pages/newProducts/modals/SelectAllActionModal';
import ImageSourcingPanel from 'components/merlin/products/ImageSourcingPanel';
import PriceColumnPopover from 'components/pages/newProducts/components/PriceColumnPopover';

// Icons
import { faAmazon } from '@fortawesome/free-brands-svg-icons';
import PinIcon from 'components/pages/newProducts/components/PinIcon';
import { faLink, faPlay, faHammer, faCheck, faArrowRight, faExternalLinkAlt, faMagic, faImage, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';

// Hooks
import useUserTabs from 'components/pages/newProducts/hooks/useUserTabs';
import useAllProductsPagination from 'components/pages/newProducts/hooks/useAllProductsPagination';
import { useProductSearch } from 'components/pages/newProducts/hooks/useProductSearch';
import useGridState from 'components/pages/newProducts/hooks/useGridState';
import { useEnrichmentProgress } from 'components/pages/newProducts/hooks/useEnrichmentProgress';
import { useSkuList } from 'components/pages/newProducts/context/SkuListContext';
import { useWorkspaceCache } from 'components/pages/newProducts/context/WorkspaceCacheContext';
import useRequestPubSub from 'hooks/useRequestPubSub';
import useKeystroke from 'hooks/useKeystroke';
//import { useSkuList } from 'components/pages/newProducts/context/SkuListContext';

// Context
import { FirebaseAuthContext } from 'context/FirebaseAuthContext.js';
import { ActionsContext } from 'context/MenuBarActionsContext';
import { useEnrichment } from 'context/EnrichmentContext';

// Enrichment Progress Panel
import EnrichmentProgressPanel from 'components/pages/newProducts/components/EnrichmentProgressPanel';

// File Menu Actions
import { createActivePricerJobs } from 'components/pages/newProducts/logic/activePricerActions';
import { createAsinDiscoveryJobs } from 'components/pages/newProducts/logic/amazonActions';
import { createShippingEstimateJobs } from 'components/pages/newProducts/logic/shipStationActions';
import { createAmazonFeeDiscoveryJobs } from 'components/pages/newProducts/logic/amazonActions';
import { pinAllProducts } from 'components/pages/newProducts/logic/bulkActionFunctions';

// 3rd Party Utils
import { isEqual } from 'lodash';

// Cloud Functions URL for API calls
const CLOUD_FUNCTIONS_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net';

// TODO Abstract to Secrets Manager
LicenseManager.setLicenseKey("Using_this_AG_Grid_Enterprise_key_( AG-049307 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( ARC Limited )_is_granted_a_( Single Application )_Developer_License_for_the_application_( Merlin )_only_for_( 1 )_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_( Merlin )_need_to_be_licensed___( Merlin )_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 16 October 2024 )____[v2]_MTcyOTAzMzIwMDAwMA==aadeefbfe111df847d24eaed4ef57023")

// Map AI grid fields to their nested Firestore enrichment paths
const AI_FIELD_MAP = {
    aiTitle: { 'enrichment.title.optimized_title': true, 'enrichment.title.edited': true },
    aiDescription: { 'enrichment.description.html_description': true, 'enrichment.description.edited': true },
    aiIngredients: { 'enrichment.metadata.ingredients': true, 'enrichment.metadata.edited': true },
    aiWeight: { 'enrichment.weight.estimated_weight_oz': true, 'enrichment.weight.edited': true },
    aiCategory: { 'enrichment.metadata.product_type': true, 'enrichment.metadata.edited': true },
    aiAllergens: { 'enrichment.metadata.allergens': true, 'enrichment.metadata.edited': true },
};

export default function AdvancedProductsGrid() {
    const { user, authLoading }= useContext(FirebaseAuthContext)
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [view] = useState('Products');
    const [selectedData, setSelectedData] = useState([]); // state for selected rows in the data grid
    const [assignmentType, setAssignmentType] = useState('pricing');
    const [selectedVariantData, setSelectedVariantData] = useState(null);
    const abortControllerRef = useRef(null);
    const [clipboard, saveToClipboard] = useState([]);
    // const { _, setSkuList } = useSkuList();
    const [allResults, setAllResults] = useState([]);

    // Search Related State
    const [searchResults, setSearchResults] = useState([]);

    // Custom Hooks
    const { gridRef, onGridReady, onColumnChanged, loadColumnState } = useGridState(user?.uid);
    const [channelStatus, userNotification, establishChannel, triggerRefetch, channelLoading, channelError] = useRequestPubSub();
    const [manualRefetch, setManualRefetch] = useState(false)
    const { skuList, setSkuList } = useSkuList();
    const { tabs, visibleTabs, tabsLoading, tabController } = useUserTabs(user?.uid, 'products');
    const { setCache, getCache, deleteCache } = useWorkspaceCache();

    // PRIMARY DATA SOURCE: useProductSearch (BigQuery-backed, loads immediately on mount)
    // This replaces the tab-dependent useAllProductsPagination for immediate data loading
    const {
        products: productSearchProducts,
        loading: productSearchLoading,
        error: productSearchError,
        currentPage,
        totalProducts,
        totalPages,
        pageSize,
        setPageSize,
        filters: productSearchFilters,
        setFilters: setProductSearchFilters,
        goToNextPage,
        goToPrevPage,
        goToPage,
        refresh: refreshProductSearch
    } = useProductSearch({ initialPageSize: 50 });

    // Legacy pagination hook kept for backwards compatibility during transition
    // TODO: Remove once useProductSearch is fully validated
    const [isAllProductsTab, setIsAllProductsTab] = useState(true); // Default to true - always use productSearch
    const paginationInitializedRef = useRef(true); // Already initialized via useProductSearch
    const {
        products: paginatedProducts,
        loading: paginationLoading,
        initialize: initializePagination,
        refresh: refreshPagination
    } = useAllProductsPagination();

    // Modal State Managers
    const [tagsModalShow, setTagsModalShow] = useState(false);
    const [assignTemplateModalShow, setAssignTemplateModalShow] = useState(false);
    const [asinDetailModalShow, setAsinDetailModalShow] = useState(false);
    const [showManagePricingTemplatesModal, setShowManagePricingTemplatesModal] = useState(false);
    const [showManageShippingTemplatesModal, setShowManageShippingTemplatesModal] = useState(false);
    const [showOpenWorkspaceModal, setShowOpenWorkspaceModal] = useState(false);
    const [showListingReadinessModal, setShowListingReadinessModal] = useState(false);

    // Confirmation Modal Handlers
    const [confirmationModalShow, setConfirmationModalShow] = useState(false);
    const [confirmationModalAction, setConfirmationModalAction] = useState(null);
    const [confirmationModalTitle, setConfirmationModalTitle] = useState(null);
    const [confirmationModalMessage, setConfirmationModalMessage] = useState(null);

    // Spreadsheet editing state
    const [selectedCount, setSelectedCount] = useState(0);
    const [filteredCount, setFilteredCount] = useState(0); // Track filtered row count for BatchScopePanel
    const [dirtyRows, setDirtyRows] = useState(new Map()); // Map of docId -> changed fields
    const [isSaving, setIsSaving] = useState(false);
    const [isPushing, setIsPushing] = useState(false);

    // Expand/Collapse and Select All state
    const [groupsExpanded, setGroupsExpanded] = useState(true);
    const [selectAllMode, setSelectAllMode] = useState(false);

    // Filter preset state
    const [activeFilterPreset, setActiveFilterPreset] = useState(null);

    // Sort state for date-based ordering
    const [activeSort, setActiveSort] = useState('');

    // Search query state - tracks current search term for composing with filters
    const [searchQuery, setSearchQuery] = useState('');

    /**
     * Handler for search input - merges search query with existing filter presets.
     * Search term is sent to BigQuery via the productSearch Cloud Function
     * which supports full-text search across SKU, title, vendor, and category.
     *
     * When clearing search, properly resets to show all products (or filtered by preset).
     */
    const handleSearchChange = useCallback((searchTerm) => {
        const trimmedTerm = (searchTerm || '').trim();
        setSearchQuery(trimmedTerm);

        // Build new filters: start with active preset filters (if any), then add search
        // This ensures clearing search properly resets to browse mode
        const newFilters = {};

        // Preserve active filter preset (e.g., shopify filter)
        if (activeFilterPreset) {
            Object.assign(newFilters, PRESET_API_PARAMS[activeFilterPreset] || {});
        }

        // Add search query if valid
        if (trimmedTerm && trimmedTerm.length >= 2) {
            newFilters.q = trimmedTerm;
            newFilters.searchMode = 'keywords';  // Match all words in any order
        }

        // Preserve active sort order
        if (activeSort) {
            newFilters.sortBy = activeSort;
        }

        console.log('[AdvancedProductsGrid] Search change:', { searchTerm: trimmedTerm, activeFilterPreset, newFilters });
        setProductSearchFilters(newFilters);
    }, [activeFilterPreset, activeSort, setProductSearchFilters]);

    /**
     * Handler for filter preset changes - connects FilterPresetBar to useProductSearch.
     * When a preset is applied, this sends the API params to the productSearch hook
     * for server-side filtering of 45k+ products.
     * Preserves any active search query when applying filter presets.
     */
    const handleFilterChange = useCallback((apiParams) => {
        console.log('[AdvancedProductsGrid] Filter change:', apiParams);
        // Start fresh with preset filters
        const newFilters = { ...apiParams };

        // Preserve active search query when changing filter presets
        if (searchQuery && searchQuery.length >= 2) {
            newFilters.q = searchQuery;
            newFilters.searchMode = 'keywords';
        }

        // Preserve active sort order
        if (activeSort) {
            newFilters.sortBy = activeSort;
        }

        setProductSearchFilters(newFilters);
    }, [searchQuery, activeSort, setProductSearchFilters]);

    /**
     * Handler for sort order changes from FilterPresetBar.
     * Triggers a new API request with the updated sortBy parameter.
     */
    const handleSortChange = useCallback((sortKey) => {
        setActiveSort(sortKey);

        // Rebuild filters with current state + new sort
        const newFilters = {};

        if (activeFilterPreset) {
            Object.assign(newFilters, PRESET_API_PARAMS[activeFilterPreset] || {});
        }

        if (searchQuery && searchQuery.length >= 2) {
            newFilters.q = searchQuery;
            newFilters.searchMode = 'keywords';
        }

        if (sortKey) {
            newFilters.sortBy = sortKey;
        }

        setProductSearchFilters(newFilters);
    }, [activeFilterPreset, searchQuery, setProductSearchFilters]);

    // Modal state for enrichment and select all action
    const [showEnrichmentModal, setShowEnrichmentModal] = useState(false);
    const [showSelectAllActionModal, setShowSelectAllActionModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [enrichmentLoading, setEnrichmentLoading] = useState(false);
    const [isApproving, setIsApproving] = useState(false);

    // Modal state for image sourcing
    const [showImageSourcingPanel, setShowImageSourcingPanel] = useState(false);

    // Enrichment progress tracking hook
    const { jobs: progressJobs, getSkipCounts, startListening, cancelJobs } = useEnrichmentProgress();

    // Enrichment context for global state (used by progress panel and global indicator)
    const { startEnrichment, updateJobs, failedSkus } = useEnrichment();

    const globalCache = {};
    
    // Redirect to login if not logged in
    useEffect(() => {
        if (authLoading) return;
        if (!user) return navigate('/login');
    }, [authLoading, user])

    // This Establishes the Real Time Messaging Channel for Async Job Updates to the UI
    useEffect(() => {
        if (!user) return
        if (channelLoading) return;
        if (channelError) {
            toast.error(channelError.message, { theme: 'colored' });
            return;
        };
        if (channelStatus === null) establishChannel(user.uid)
    }, [channelStatus, channelLoading, channelError, user]);

    // This will pop a toast with whatever message is received when the channel is triggered
    /**
    useEffect(() => {
        if (channelLoading) return;
        if (!channelStatus || channelStatus === 'idle') return;
        if (userNotification) {
            toast[userNotification.variant](userNotification.message, { theme: 'colored' });
        }
    }, [triggerRefetch])
     */

    // PRIMARY: Update grid when productSearch products change
    // This loads data immediately on mount - NO TAB DEPENDENCY
    useEffect(() => {
        if (!gridRef.current?.api) {
            console.log('[Grid] Grid API not ready');
            return;
        }

        console.log('[Grid] ProductSearch data effect:', {
            count: productSearchProducts.length,
            loading: productSearchLoading,
            error: productSearchError
        });

        if (productSearchLoading) {
            gridRef.current.api.showLoadingOverlay();
        } else if (productSearchProducts.length > 0) {
            console.log('[Grid] Setting row data with', productSearchProducts.length, 'products from productSearch');
            gridRef.current.api.setRowData(productSearchProducts);
            gridRef.current.api.hideOverlay();
        } else {
            console.log('[Grid] No products, showing no rows overlay');
            gridRef.current.api.setRowData([]);
            gridRef.current.api.showNoRowsOverlay();
        }
    }, [productSearchProducts, productSearchLoading, productSearchError]);

    // Handle productSearch errors
    useEffect(() => {
        if (productSearchError) {
            toast.error(`Error loading products: ${productSearchError}`, { theme: 'colored' });
        }
    }, [productSearchError]);

    // Sync productSearchProducts to SkuListContext
    // This keeps MerlinAiChatbox and TabContainer working with the current grid contents
    useEffect(() => {
        if (productSearchProducts?.length > 0) {
            const skuResults = productSearchProducts.map(p => ({
                sku: p.sku || p.parentSku,
                score: 100,
                source: 'bigquery'
            }));
            setSkuList(skuResults);
        }
    }, [productSearchProducts, setSkuList]);

    // Sync enrichment progress jobs to context for global access
    useEffect(() => {
        if (Object.keys(progressJobs).length > 0) {
            updateJobs(progressJobs);
        }
    }, [progressJobs, updateJobs]);

    // Refresh grid when failedSkus changes (for row highlighting)
    useEffect(() => {
        if (gridRef.current?.api && failedSkus.length > 0) {
            gridRef.current.api.redrawRows();
        }
    }, [failedSkus]);

    // Refresh enrichment columns when progressJobs changes
    useEffect(() => {
        if (gridRef.current?.api && Object.keys(progressJobs).length > 0) {
            gridRef.current.api.refreshCells({
                columns: ['enrichmentStatus', 'aiTitle', 'aiDescription', 'aiIngredients', 'aiWeight', 'aiCategory', 'aiAllergens'],
                force: true
            });
        }
    }, [progressJobs]);

    // LEGACY: Tab-dependent initialization (kept for backwards compatibility)
    // Auto-initialize All Products tab when it becomes active
    // Note: This is now a no-op since useProductSearch loads data immediately
    useEffect(() => {
        const isAllProductsActive = tabController.activeTab === 'system-all-products';

        if (isAllProductsActive && !isAllProductsTab) {
            console.log('[Grid] All Products tab is active (legacy check)');
            setIsAllProductsTab(true);
        } else if (tabController.activeTab && !isAllProductsActive && isAllProductsTab) {
            // Note: With new architecture, data stays loaded when switching tabs
            console.log('[Grid] Tab switch detected (legacy)');
        }
    }, [tabController.activeTab, isAllProductsTab]);

    // TODO - abstract to CSS file and rebuild CSS
    applyHeaderStyle();
    applyRowStyle();

    const fetchProducts = useCallback(async (complexSkuList) => {

        const cachedProducts = getCache(complexSkuList);


        if (cachedProducts) {
            return cachedProducts;
        }

        // setLoading(true);

        const bigSkuList = complexSkuList.map((searchResult) => searchResult.sku);
        const chunks = chunkArray(bigSkuList, 10);

        const variantPromises = [];
        const parentPromises = [];

        for (const skuList of chunks) {

            // data collection
            const variantsGroupRef = collectionGroup(db, "variants");
            variantPromises.push( getDocs(
                query(variantsGroupRef,
                    where("parentSku", "in", skuList),
                ))
            );
            const parentProductsRef = collection(db, "productCatalog/topicz/products");
            parentPromises.push( getDocs(
                query(parentProductsRef,
                    where("sku", "in", skuList),  
                ))
            );
            
        }

        const variantsSnapshots = await Promise.all(variantPromises);
        const parentProductsSnapshots = await Promise.all(parentPromises);

        // data processing
        const variants = [];
        const seenSkus = new Set();
        variantsSnapshots.forEach((variantSnapshot) => {
            variantSnapshot.forEach((doc) => {
                let data = doc.data();
                // Skip duplicate variants (same SKU from different collections)
                if (data.sku && seenSkus.has(data.sku)) return;
                if (data.sku) seenSkus.add(data.sku);
                data.docId = doc.id;
                data._firestorePath = doc.ref.path;
                variants.push(data);
            });
        });

        const parentProducts = [];
        parentProductsSnapshots.forEach((parentProductSnapshot) => {
            parentProductSnapshot.forEach((doc) => {
                let data = doc.data();
                data.docId = doc.id;
                parentProducts.push(data);
            });
        });

        // merge the parentData into the variants array
        for (const variant of variants) {
            for (const parentProduct of parentProducts) {
                if (variant.parentSku === parentProduct.sku) {
                    // copy the parent data into the variant
                    // property by property. If the property already exists on the variant
                    // log an error
                    for (const property in parentProduct) {
                        if (variant[property]) {
                            variant[`parent_${property}`] = parentProduct[property];
                        } else {
                            variant[property] = parentProduct[property];
                        }
                    }
                }
            }
            for (const sourceProduct of complexSkuList) {
                const sku = sourceProduct.sku;
                const source = sourceProduct.source;
                const score = sourceProduct.score;
                if (variant.parentSku === sku) {
                    variant.searchSource = source;
                    variant.searchScore = score;
                }
            }
        }
        const finalProducts = variants;

        // cache the results
        setCache(complexSkuList, finalProducts);
        
        // setLoading(false);

        return finalProducts;

    }, [skuList, db]);

    const flattenResults = (results) => {
        // make sure results is iterable
        if (!results) return [];
        if (!Array.isArray(results)) return [];
        const flattenedResults = [];
        for (const result of results) {
            flattenedResults.push(result);
            // if (Array.isArray(result)) {
            //     for (const product of result) {
            //         flattenedResults.push(product);
            //     }
            // } else {
            //     // If result is not an array, handle it appropriately
            //     // For example, you might want to log a warning or throw an error
            //     console.warn('Expected result to be an array, received:', result);
            // }
        }
        return flattenedResults;
    }

    // when the list of result skus changes, fetch the products from firebase
    useEffect(() => {
        // Skip this effect for All Products tab - it uses server-side pagination instead
        if (isAllProductsTab) {
            return;
        }

        const abortController = new AbortController();
        const signal = abortController.signal;

        const fetchData = async () => {
            if (signal.aborted) {
                console.log("fetchData aborted before starting");
                return;
            }

            try {
                setLoading(true);
                if (gridRef.current) {
                    gridRef.current.api.showLoadingOverlay();
                }
                const bigChunks = chunkArray(skuList, 100);
                let allResults = [];
                for (const chunk of bigChunks) {
                    if (signal.aborted) {
                        console.log("fetchData aborted during execution");
                        setLoading(false)
                        if (gridRef.current) {
                            gridRef.current.api.hideOverlay();
                        }
                        return;
                    }
                    try {
                        const results = await fetchProducts(chunk);
                        const newResults = flattenResults(results);
                        allResults = allResults.concat(newResults);
                    } catch (error) {
                        if (error.name === 'AbortError') {
                            console.log('fetchData aborted due to AbortError');
                            return;
                        }
                        console.log(error);
                        setLoading(false);
                        if (gridRef.current) {
                            gridRef.current.api.hideOverlay();
                        }
                    }
                }
                if (!signal.aborted && gridRef.current) {
                    loadColumnState();
                    setAllResults(allResults);
                    gridRef.current.api.setRowData(allResults);
                    gridRef.current.api.refreshCells({ force: true });
                    setLoading(false);
                    gridRef.current.api.hideOverlay();
                }
            } catch (error) {
                console.log(error);
            }
        };

        if (skuList?.length > 0) {
            fetchData();
        } else {
            if (gridRef.current) {
                gridRef.current?.api?.setRowData([]);
                gridRef.current?.api?.refreshCells({ force: true });
                gridRef.current?.api?.hideOverlay();
            }
        }

        // Cleanup function
        return () => {
            abortController.abort();
        };
    }, [skuList, view, gridRef, fetchProducts, isAllProductsTab]); // Ensure all necessary dependencies are listed

    /** refreshes the grid when signaled via triggerRefetch */
    useEffect(() => {

        const refreshData = async () => {
            
            const chunks = chunkArray(skuList, 10);
            let allResults = [];
            for (const chunk of chunks) {
                const results = await fetchProducts(chunk);
                allResults = allResults.concat(flattenResults(results));
            }
            allResults.sort((a, b) => b.searchScore - a.searchScore);
                
            if (gridRef.current) {
                // get current grid data
                const currentRowsMap = new Map();
                gridRef.current.api.forEachNode(node => {
                    if (!node.group) currentRowsMap.set(node.id, node.data)
                });
                const transaction = {
                    update: [],
                };
                allResults.forEach((newRow) => {
                    const currentRow = currentRowsMap.get(newRow.docId);
                    if (currentRow && !isEqual(currentRow, newRow)) {
                        transaction.update.push(newRow);
                    }
                });
                if (transaction.update.length > 0) {
                    try {
                        const result = gridRef.current.api.applyTransaction(transaction);
                        
                        if (result && result.update) {
                            gridRef.current.api.refreshCells({ rowNodes: result.update });
                            const groupNodesToRefresh = new Set();
                            result.update.forEach(node => {
                                let groupNode = node.parent;
                                while (groupNode && groupNode.level >= 0) {
                                    groupNodesToRefresh.add(groupNode);
                                    groupNode = groupNode.parent;
                                }
                            });
                            gridRef.current.api.refreshCells({ rowNodes: Array.from(groupNodesToRefresh) });
                            // loadColumnState();
                        }
                    } catch (error) {
                        console.log("transaction error", error)
                    }
                }
            }
        }
        if (skuList?.length > 0) { // Ensure skuList is not empty
            refreshData();
        }
    }, [triggerRefetch, manualRefetch])

    /** Custom Cell Renderers TODO - abstract these into their own files */

    const ProductGroupCellRenderer = forwardRef( (props, ref) => { // (props) => {
        const { node, api } = props;
        if (!node.group) {
            return null;  // not a group row, nothing to render
        }
        const [animate, setAnimate] = useState(false);
        const [expanded, setExpanded] = useState(false);
        const [checked, setChecked] = useState(false);
        const [pinned, setPinned] = useState(false);
        const timeoutRef = useRef(null);
        const animationLength = 1500; // Duration of animation in ms

        useEffect(() => {
            if (!node) return;
            if (node.expanded) {
                setExpanded(true);
            } else {
                setExpanded(false);
            }
            if (node.allLeafChildren[0]?.data.searchSource === 'exactSku') {
                toggleExpanded();
            }
            const isPinned = findPinnedStatus();
            setPinned(isPinned);
        }, [node]);

        useEffect(() => {
            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }, []);

        const parentSku = node.key;  // Assume the key holds the parentSku
        const productName = node.allLeafChildren[0]?.data.name || node.allLeafChildren[0]?.data.title;
        const productImage = node.allLeafChildren[0]?.data.primaryImage || node.allLeafChildren[0]?.data.thumbnail;
        // Extract parent document ID from Firestore path for product URL
        // Path format: productCatalog/topicz/products/{parentDocId}/variants/{variantDocId}
        const firestorePath = node.allLeafChildren[0]?.data._firestorePath || '';
        const pathMatch = firestorePath.match(/\/products\/([^/]+)/);
        const parentDocId = pathMatch ? pathMatch[1] : node.allLeafChildren[0]?.data.parent_docId || parentSku;
        // Construct product URL - route is /:vendor/products/:productId (uses Firestore doc ID)
        const productUrl = `/topicz/products/${parentDocId}`;
        const sourceInfo = node.allLeafChildren[0]?.data.searchSource;

        const dynamicStyle = {
            alignItems: 'center',
            padding: '0',
            backgroundColor: animate ? 'green' : 'white',
            transition: `background-color ${Number(animationLength/1000).toFixed(1)}s`
        };

        const toggleExpanded = () => {
            node.setExpanded(!node.expanded);
            setExpanded(!expanded);
        };

        const handleCheckboxChange = () => {
            const newCheckedState = !checked;
            setChecked(newCheckedState);
        
            const currentlySelectedNodes = gridRef.current.api.getSelectedNodes();
            const newSelectedNodes = new Set(currentlySelectedNodes);
            
            // Update the selected state of all rows within the current group.
            node.allLeafChildren.forEach(rowNode => {
                if (newCheckedState) {
                    newSelectedNodes.add(rowNode);
                } else {
                    newSelectedNodes.delete(rowNode);
                }
            });
            
            // Set the selected state of each row.
            newSelectedNodes.forEach(rowNode => rowNode.setSelected(true));
            currentlySelectedNodes
                .filter(rowNode => !newSelectedNodes.has(rowNode))
                .forEach(rowNode => rowNode.setSelected(false));
        };

        const findPinnedStatus = () => {
            if (!node.allLeafChildren) return false;
            let foundPin = false;
            for (const child of node.allLeafChildren) {
                if (child.data.searchSource === 'pinned') foundPin = true;
            }
            return foundPin;
        }

        useImperativeHandle(ref, () => ({
            refresh: (params) => {
                setAnimate(true); // Trigger animation
                // clear any existing timeouts
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                // set new timeout and store id
                timeoutRef.current = setTimeout(() => {
                    setAnimate(false)
                }, animationLength); // Duration of animation
                return true; // Return false to indicate the cell should be rerendered
            }
        }));

        /**<div>
                    <Button variant='outline-default' size='sm' className='border-0 p-0 me-1'>
                        <PinIcon pinned={findPinnedStatus} togglePin={pinned ? handleRemovePinProduct : handleAddPinProduct}/>
                    </Button>
                </div> */

        return (
            <div className="ms-1 d-flex justify-content-between" style={dynamicStyle}>
                <div className="d-inline-flex" style={{ alignItems: 'center', fontSize: '0.70rem' }}>
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={handleCheckboxChange}
                        className='me-1 group-checkbox'
                        style={{ margin: '0', padding: '0', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <Button onClick={toggleExpanded} variant='outline-default' size='sm' className='border-0 p-0 me-1'>
                        {expanded ? '-' : '+'}
                    </Button>
                    <ImageCellRenderer value={productImage} data={node.allLeafChildren[0]?.data} />
                    { sourceInfo === 'exactSku' && <SoftBadge bg="success" className="me-1">Exact SKU</SoftBadge> }
                    <span className='ms-2 me-1'>{parentSku} {productName} ({node.allChildrenCount})</span>
                    <a href={productUrl} target="_blank" rel="noopener noreferrer" className='me-1' style={{ lineHeight: '1' }}>
                        <FontAwesomeIcon icon={faLink} size='xs'/>
                    </a>
                </div>
                
            </div>
        );
    });

    // Helper to extract image from document data
    const extractImage = (docData) => {
        if (!docData) return null;
        const img = docData.image ||
            docData.primaryImg ||
            docData.primaryImage ||
            docData.files?.[0]?.src ||
            docData.images?.[0]?.src ||
            docData.images?.[0] ||
            docData.thumbnail ||
            null;
        return typeof img === 'string' ? img : img?.src || null;
    };

    function ImageCellRenderer(params) {
        const { data } = params;
        const additionalImages = data?.files || [];
        const [modalShow, setModalShow] = useState(false);
        const [imageUrl, setImageUrl] = useState(params.value || null);
        const [isLoading, setIsLoading] = useState(false);

        // Lazy-load image from Firestore if not available from BigQuery
        useEffect(() => {
            let isMounted = true;

            // Already have an image, no need to fetch
            if (params.value) {
                setImageUrl(params.value);
                return;
            }

            // Need Firestore path to fetch
            const firestorePath = data?._firestorePath;
            if (!firestorePath) {
                return;
            }

            // Fetch image from Firestore - try variant first, then parent product
            const fetchImage = async () => {
                if (!isMounted) return;
                setIsLoading(true);
                try {
                    // First try the variant document
                    const docRef = doc(db, firestorePath);
                    const docSnap = await getDoc(docRef);
                    if (!isMounted) return;

                    if (docSnap.exists()) {
                        const docData = docSnap.data();
                        let img = extractImage(docData);

                        // If variant has no image, try the parent product
                        if (!img && firestorePath.includes('/variants/')) {
                            // Extract parent path: productCatalog/topicz/products/{parentId}
                            const parentPath = firestorePath.split('/variants/')[0];
                            const parentRef = doc(db, parentPath);
                            const parentSnap = await getDoc(parentRef);
                            if (!isMounted) return;

                            if (parentSnap.exists()) {
                                img = extractImage(parentSnap.data());
                            }
                        }

                        if (img && isMounted) {
                            setImageUrl(img);
                        }
                    }
                } catch (error) {
                    console.error('[ImageCellRenderer] Error fetching image:', error);
                }
                if (isMounted) setIsLoading(false);
            };

            fetchImage();

            return () => {
                isMounted = false;
            };
        }, [params.value, data?._firestorePath]);

        if (isLoading) {
            return <div style={{ width: 20, height: 20, background: '#f0f0f0' }} />;
        }

        return imageUrl ?
            <div>
                <Image
                    src={imageUrl}
                    alt="product"
                    width={20}
                    fluid
                    onClick={() => setModalShow(true)}
                    style={{maxHeight: '20px'}}
                />
                <ImageModal
                    show={modalShow}
                    handleClose={() => setModalShow(false)}
                    imgSrc={imageUrl}
                    images={additionalImages}
                    productRef={data?.ref}
                />
            </div>
            : null;
    }

    const ImageModal = ({ show, handleClose, imgSrc, images, productRef }) => {
        if (!show || !imgSrc) return null;
        
        async function updatePrimaryImage(productRef, newSrc) {
            if (!productRef) return;
            try {
                await updateDoc(productRef, { primaryImage: newSrc });
            } catch (error) {
                toast.error('Error updating primary image', { theme: 'colored' });
                return null;
            }
        }
      
        return (
            <Modal show={show} onHide={handleClose}>
              <Modal.Header closeButton>
                <Modal.Title>Image Preview</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Row>
                    <Col>
                        <Image src={imgSrc} alt="Enlarged" fluid/>
                    </Col>
                </Row>
                {images.length > 0 &&
                    <Row>
                        {images.map((image, index) => (
                            <Col key={index}>
                                <Image 
                                    width={75}
                                    src={image.src}
                                    alt={image.alt}
                                    fluid
                                    thumbnail
                                    
                                />
                            </Col>
                        ))}
                    </Row>
                }
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
        );
    };

    const TagCellRenderer = forwardRef( ({ data }, ref) => {     
        const [tags, setTags] = useState(data.tags || []);
        const [isAdding, setIsAdding] = useState(false);
        const [newTag, setNewTag] = useState('');
        const [animateBackground, setAnimateBackground] = useState(false);
        const timeoutRef = useRef(null)
        const backgroundColor = animateBackground ? 'green' : 'white';

        useEffect(() => {
            setTags(data.tags || []);
        }, [data]);

        // refresh method for AG-Grid
        useImperativeHandle(ref, () => ({
            refresh: (params) => {
                setTags(params.data.tags || []);
                setAnimateBackground(true); // Trigger animation
                // clear any existing timeouts
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                // set new timeout and store id
                timeoutRef.current = setTimeout(() => {
                    setAnimateBackground(false)
                }, 5000); // Duration of animation
                return true; // Return false to indicate the cell should be rerendered
            }
        }));

        // Cleanup on unmount
        useEffect(() => {
            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }, []);

        // the product's reference (may be undefined for BigQuery-sourced data)
        const variantRef = data.ref;
        const canEdit = !!variantRef;

        const handleAddTag = async (newTag) => {
            if (!canEdit) {
                toast.warning('Cannot edit - no Firestore reference', { theme: 'colored' });
                return;
            }
            const updatedTags = [...tags, newTag];
            setTags(updatedTags);
            try {
                await updateDoc(variantRef, { tags: updatedTags });
                setNewTag('');
                setIsAdding(false);
            } catch (error) {
                toast.error('Error updating tags', { theme: 'colored' });
                return null;
            }
        }

        const handleRemoveTag = async (tagToRemove) => {
            if (!canEdit) {
                toast.warning('Cannot edit - no Firestore reference', { theme: 'colored' });
                return;
            }
            const updatedTags = tags.filter(tag => tag !== tagToRemove);
            setTags(updatedTags);
            try {
                await updateDoc(variantRef, { tags: updatedTags });
            } catch (error) {
                toast.error('Error updating tags', { theme: 'colored' });
                return null;
            }
        }

        const handleInputChange = (event) => {
            setNewTag(event.target.value);
        }

        const handleInputKeyPress = (event) => {
            if (event.key === 'Enter') {
                handleAddTag(newTag);
            }
        }

        return (
            <div 
                className="d-flex flex-wrap justify-content-start align-items-center"
                style={{ height: '100%', backgroundColor: backgroundColor, transition: 'background-color 5s' }} 
            >
                {tags.map(tag => (
                    <div key={tag}
                      className="d-flex flex-wrap justify-content-start align-items-center" 
                    >
                        <TagDisplay tag={tag} onRemove={() => handleRemoveTag(tag)} />
                    </div>
                ))}
                {isAdding ?
                    <Form.Control
                        type="text"
                        value={newTag}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyPress}
                        onBlur={() => setIsAdding(false)}
                        autoFocus
                        size="sm"
                        className="d-inline-block w-auto"
                    />
                    :
                    <div
                        style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            backgroundColor: 'green',
                            color: 'white',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}
                        
                        onClick={() => {setIsAdding(true); setNewTag('')}}
                    >+</div>
                }
            </div>
        );

    });

    function ListingsCellRenderer({ data }) {
        const [showNewModal, setShowNewModal] = useState(false);
        const { listings } = data;

        return (
            <>
            <div className="d-flex flex-wrap justify-content-start align-items-center" style={{ height: '100%' }}>
                {listings?.length > 0 ?
                    listings.map(listing => (
                        <div key={listing.id} className="position-relative d-flex align-items-center m-1">
                            <ListingDisplay listing={listing} />
                        </div>
                    ))
                    :
                    <div
                        as="button"
                        style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            backgroundColor: 'green',
                            color: 'white',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}
                        onClick={() => setShowNewModal(true)}
                    >+</div>
                }
            </div>
            <NewListingModal show={showNewModal} setShow={setShowNewModal} data={data} />
            </>

        )
    }

    // Channels cell renderer - shows logos for active sales channels
    function ChannelsCellRenderer({ data }) {
        const hasShopify = data?.shopify_product_id || data?.shopify?.productId;
        const hasAmazon = data?.amazonAsin || data?.amazon?.asin;

        return (
            <div className="d-flex align-items-center gap-1" style={{ height: '100%' }}>
                {hasShopify && (
                    <img
                        src="/img/shopify.png"
                        alt="Shopify"
                        title="Listed on Shopify"
                        style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                )}
                {hasAmazon && (
                    <img
                        src="/img/amazon.png"
                        alt="Amazon"
                        title="Has ASIN"
                        style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                )}
                {!hasShopify && !hasAmazon && (
                    <span style={{ color: '#6c757d', fontSize: '12px' }}>—</span>
                )}
            </div>
        );
    }

    function AsinDataRenderer(row) {
        const { data, node, api } = row;
        const variantRef = data.ref;
        const [snapData, setSnapData] = useState(null);
        useEffect(() => {
            let isMounted = true;
            if (!variantRef) return;
            const unsubscribe = onSnapshot(variantRef, (doc) => {
                if (doc.exists()) {
                    const data = doc.data();
                    const parentRef = data.parentRef;
                    getDoc(parentRef).then((parentDoc) => {
                        const parentData = parentDoc.data();
                        data.barcodes = parentData.barcodes;
                        if (isMounted) setSnapData(data);
                    });
                }
            });
            return () => {
                isMounted = false;
                unsubscribe();
            }
        }, [variantRef]);

        const handleModal = (data) => {
            setAsinDetailModalShow(true);
            setSelectedVariantData(data);
        }

        return (
            
            <div className="d-flex flex-wrap justify-content-start align-items-center" style={{ height: '100%' }}>
                {data.amazon?.asin ?
                    <div onClick={() => handleModal(snapData)}>
                        {data.amazon?.asin !== 'Not Found' ?
                            <SoftBadge 
                                bg="success"
                                className="me-1"
                            >
                                {data.amazon.asin}
                            </SoftBadge>
                            :
                            <SoftBadge
                                bg="danger"
                                className="me-1"
                            >
                                {data.amazon.asin}
                            </SoftBadge>
                        }
                    </div>
                    :
                    <div
                        style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            backgroundColor: 'red',
                            color: 'white',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}
                        
                    ><FontAwesomeIcon icon={faHammer} onClick={() => createSingleAsinDiscoveryJob(snapData)} /></div>
                }
            </div>
            
        )

        async function createSingleAsinDiscoveryJob(snapData) {
            console.log("job created", snapData)
            try {
                await addDoc(collection(db, 'amazonApiTasks'), {
                    status: "New",
                    handler: "amazonApiRequestHandler",
                    type: "gatherAsinIntel",
                    jobData: {
                    contentType: "application/json",
                    httpMethod: "POST",
                    inSeconds: 10,
                    payload: { // sku, listPrice, shippingCost, asin
                        sku: snapData.sku,
                        title: snapData.title,
                        type: "UPC",
                        values: [
                            {size: "unit", value: snapData.barcodes.unit}, 
                            {size: "stock", value: snapData.barcodes.stock}, 
                            {size: "other", value: snapData.barcodes.other}
                        ],
                    },
                    queueName: "amazon-api-queue",
                    },
                    timestamp: {
                        created: serverTimestamp(),
                        updated: serverTimestamp(),
                    }
                });
                toast.success('ASIN Discovery Job Created', { theme: 'colored' });
                console.log("here")
                return { success: true, message: 'Job created' };
            } catch (error) {
                console.log
                toast.error(error.message, { theme: 'colored' });
            }
        }
    }
    
    function ShippingDataRenderer(row) {
        const shippingMaster = row.node.data?.shippingMaster || {};
        const average = row.node.data?.estShippingCost || 0;

        return (
            average !== 0 ?
                <div className="d-flex flex-wrap justify-content-start align-items-center">
                    ${average}
                </div>
            :   <div className="d-flex flex-wrap justify-content-start align-items-center">
                    <FontAwesomeIcon icon={faHammer} />
                </div>
        )
    }

    function CurrentTitleRenderer(row) {
        const { data, node, api } = row;
        const { title, alternateTitles, previousTitles, ref } = data;
        const [displayTitle, setDisplayTitle] = useState();
        const displayTitles = [];
        if (title) displayTitles.push(title);
        if (alternateTitles) displayTitles.push(...alternateTitles);
        if (previousTitles) displayTitles.push(...previousTitles);
        const [showModal, setShowModal] = useState(false);

        useEffect(() => {
            if (displayTitles.length > 0) {
                setDisplayTitle(displayTitles[0]);
            }
        }, [displayTitles]);
        
        const saveTitle = async (e, title) => {
            console.log(title)
            e.preventDefault();
            try {
                setDisplayTitle(title);
                await updateDoc(ref, { title })
                console.log(user.uid)
                // triggerRefetch();
                // create random string
                const randomString = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
                storageSet(storageRef(realtime, `reqMessages/${user.uid}`),{
                    message: 'Product title updated',
                    status: `success-${randomString}`,
                    userId: user.uid,
                })
                setShowModal(false);
            } catch (error) {
                toast.error(error.message, { theme: 'colored' });
                console.log(error)
            }
            
        }

        const Actions = ({title}) => (
            <div className="end-0 top-50 pe-3 translate-middle-y hover-actions">
              <Button variant="light" size="sm" className="border-300 me-1 text-600" onClick={(e) => saveTitle(e, title)}>
                <FontAwesomeIcon icon="check" />
              </Button>
              <Button variant="light" size="sm" className="border-300 text-600">
                <FontAwesomeIcon icon="trash-alt" />
              </Button>
            </div>
        );
          
        const HoverableTitleTable = ({titles}) => {
        
            const LineItem = ({ title, index }) => (
                <tr className="hover-actions-trigger" key={index}>
                    <td>{title}</td>
                    <td className="w-auto">
                        <Actions title={title}/>
                    </td>
                </tr>
            );

            return (
                <Table hover responsive>
                <thead>
                    <tr>
                    <th scope="col">Title</th>
                    </tr>
                </thead>
                <tbody>
                    {titles.map((title, index) => (
                        <LineItem key={index} title={title} />
                    ))}
                </tbody>
                </Table>
            );
        };
          
        
        return (
            <>
            <div className="d-inline-block flex-wrap justify-content-start align-items-center text-truncate" style={{ width: '250px'}}>
                {displayTitle}
            </div>
            <FontAwesomeIcon icon={faExternalLinkAlt} onClick={() => setShowModal(true)} />
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Choose an Alternate Title</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-wrap justify-content-start align-items-center">
                        <HoverableTitleTable titles={displayTitles} />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
            </>  
        )
    }

    function CurrentDescriptionRenderer(row) {
        const { data, node, api } = row;
        const { currentDescription, descriptionOptions, previousDescriptions, ref } = data;
        const [displayDescription, setDisplayDescription] = useState();
        const displayDescs = [];
        if (currentDescription) displayDescs.push({currentDescription: currentDescription});
        if (descriptionOptions) {
            descriptionOptions.forEach((desc) => {
                displayDescs.push({currentDescription: desc});
            });
        }
        if (previousDescriptions) {
            previousDescriptions.forEach((desc) => {
                displayDescs.push({currentDescription: desc});
            });
        }
        const [showModal, setShowModal] = useState(false);

        // give a key to each element in displayDescs
        // useEffect(() => {
        //     if (displayDescs) {
        //         const tableDataMap = displayDescs.map((desc) => {
        //             return { currentDescription: desc }
        //         })
        //         setTableData(tableDataMap);
        //     }
        // }, [displayDescs]);

        useEffect(() => {
            if (displayDescs.length > 0) {
                setDisplayDescription(displayDescs[0].currentDescription);
            }
        }, [displayDescs]);
        
        const saveDesc = async (e, desc) => {
            e.preventDefault();
            try {
                setDisplayDescription(desc);
                await updateDoc(ref, { currentDescription: desc })
               const randomString = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
                storageSet(storageRef(realtime, `reqMessages/${user.uid}`),{
                    message: 'Product description updated',
                    status: `success-desc-${randomString}`,
                    userId: user.uid,
                })
                setShowModal(false);
            } catch (error) {
                toast.error(error.message, { theme: 'colored' });
                console.log(error)
            }
            
        }

        const Actions = ({desc}) => (
            <div className="end-0 top-50 pe-3 translate-middle-y hover-actions">
              <Button variant="light" size="sm" className="border-300 me-1 text-600" onClick={(e) => saveDesc(e, desc)}>
                <FontAwesomeIcon icon="check" />
              </Button>
              <Button variant="light" size="sm" className="border-300 text-600">
                <FontAwesomeIcon icon="trash-alt" />
              </Button>
            </div>
        );
          
        const HoverableDescTable = ({descs}) => {
        
            const LineItem = ({ desc, index }) => (
                <tr className="hover-actions-trigger" key={index}>
                    <td>{desc.currentDescription}</td>
                    <td className="w-auto">
                        <Actions desc={desc}/>
                    </td>
                </tr>
            );

            return (
                <Table hover responsive>
                <thead>
                    <tr>
                    <th scope="col">Title</th>
                    </tr>
                </thead>
                <tbody>
                    {descs.map((desc, index) => (
                        <LineItem key={index} desc={desc} />
                    ))}
                </tbody>
                </Table>
            );
        };

        function AdvanceDescriptionTable({ descriptions }) {
        
            const columns = [
                {
                    accessor: 'currentDescription',
                    Header: 'Description',
                    
                }
            ];

            return(
                <AdvanceTableWrapper
                    columns={columns}
                    data={descriptions}
                    sortable
                    pagination
                    perPage={3}
                >
                <AdvanceTable
                    table
                    headerClassName="bg-200 text-nowrap align-middle"
                    rowClassName="align-middle white-space-nowrap"
                    tableProps={{
                        bordered: true,
                        striped: true,
                        className: 'fs-10 mb-0'
                    }}
                />
                <div className="mt-3">
                    <AdvanceTablePagination
                    table
                    />
                </div>
                </AdvanceTableWrapper>
            )
        }
        
        return (
            <>
            <div className="d-inline-block flex-wrap justify-content-start align-items-center text-truncate" style={{ width: '250px'}}>
               {displayDescription}
            </div>
            <FontAwesomeIcon icon={faExternalLinkAlt} onClick={() => setShowModal(true)} />
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Choose a different Description</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-wrap justify-content-start align-items-center">
                        <HoverableDescTable descs={displayDescs} />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
            </>  
        )
    }

    /** Grid Components - these are components used in the custom cell renderers */

    function TagDisplay({ tag, onRemove }) {
        return (
            <div
                className="d-inline-flex align-items-center border border-1 rounded-2 border-500 fs--2 text-900"
                style={{maxHeight: '20px'}}
            >
                <span className="ms-1 me-1">{tag}</span>
                <div variant="link"
                    style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: 'white',
                            color: 'red',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '0.5rem', 
                            lineHeight: '0.5rem'
                        }}
                    className='p-2 me-1'
                    size="sm" onClick={onRemove}
                >x</div>
            </div>
        )
    }

    function ListingDisplay({ listing }) {
        
        const ListingIcon = ({ listing }) => {
            switch (listing.channel) {
                case 'amazon': return <div
                                style={{
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    backgroundColor: 'white',
                                    color: listing.status == 'active' ? 'green' : 'red',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                }}><FontAwesomeIcon icon={faAmazon} />
                                </div>;
                default: return null;
            }
        }

        return (
            <div
                style={{minWidth: '20px', minHeight: '20px'}}
                key={listing.id}
            >
                <ListingIcon listing={listing} />
            </div>
        )
    }

    // TODO - this needs to be updated to account for both the variant and product
    // refs being included in the same grid row, if a variant field is changed
    // then this function will erroneously update the product ref and not variant ref
    const onCellValueChanged = async (params) => {
        const parentDocId = params.data.parent_docId;
        if (params.newValue !== params.oldValue) {
            const docRef = doc(db, `productCatalog/topicz/products/${parentDocId}/variants`, params.data.docId);
            await updateDoc(docRef, { [params.colDef.field]: params.newValue });
        }
    };

    const LoadingAnimationComponent = ({size}) => {
        if (!size) size = 300;
        return (
            <div><Lottie animationData={dotsAnimation} style={{height: size}} /></div>
        )
    }
      
    /** Column Definitions */
    const basicColumnDefs = [
        { field: 'parentSku',
          headerName: 'Parent',
          filter: true,
          rowGroup: true,
          hide: true,
        },
        { headerName: '', checkboxSelection: true, width: 40, pinned: 'left', lockPosition: true, suppressMenu: true, headerComponent: 'expandCollapseHeader' },
        { field: 'primaryImage', headerName: 'Image', cellRenderer: ImageCellRenderer, width: 60, pinned: 'left' },
        { field: 'sku', headerName: 'SKU', filter: true, width: 120, pinned: 'left' },
        {
            headerName: 'AI',
            width: 45,
            pinned: 'left',
            cellRenderer: (params) => {
                const hasEnrichment = params.data?.enrichment &&
                    Object.keys(params.data.enrichment).length > 0;
                if (!hasEnrichment) return null;

                // Build tooltip showing which modes are complete
                const modes = Object.keys(params.data.enrichment);
                const tooltipText = `AI Enriched: ${modes.join(', ')}`;

                return (
                    <div title={tooltipText} style={{ cursor: 'pointer' }}>
                        <FontAwesomeIcon
                            icon={faMagic}
                            style={{ color: '#9b59b6' }}
                        />
                    </div>
                );
            },
            suppressMenu: true,
        },
        {
            field: 'enrichmentStatus',
            headerName: 'Enrich',
            width: 95,
            cellRenderer: (params) => {
                // Get status from progressJobs or from enrichment data
                const sku = params.data?.parentSku || params.data?.sku;
                const job = (params.context?.progressJobs || progressJobs)?.[sku];
                const status = job?.status || params.data?.enrichment?.status;

                if (!status) return <span style={{ color: '#999', fontSize: '11px' }}>—</span>;

                const statusConfig = {
                    pending: { bg: '#6c757d', label: 'Pending' },
                    processing: { bg: '#17a2b8', label: 'Processing' },
                    completed: { bg: '#ffc107', label: 'Review', color: '#000' },
                    partial: { bg: '#fd7e14', label: 'Partial' },
                    approved: { bg: '#28a745', label: 'Approved' },
                    pushed: { bg: '#007bff', label: 'Pushed' },
                    failed: { bg: '#dc3545', label: 'Failed' }
                };

                const config = statusConfig[status] || { bg: '#6c757d', label: status };

                return (
                    <span style={{
                        backgroundColor: config.bg,
                        color: config.color || '#fff',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '10px',
                        fontWeight: '600'
                    }}>
                        {config.label}
                    </span>
                );
            },
            valueGetter: (params) => {
                const sku = params.data?.parentSku || params.data?.sku;
                const job = (params.context?.progressJobs || progressJobs)?.[sku];
                return job?.status || params.data?.enrichment?.status || '';
            }
        },
        {
          field: 'computedStatus',
          headerName: 'Status',
          width: 140,
          editable: false,
          cellRenderer: StatusBadgeRenderer,
          filter: 'agSetColumnFilter',
          valueGetter: (params) => {
            if (!params.data) return '';
            const result = computeProductStatus(params.data);
            return result.label;
          },
        },
        {
          field: 'shopify_product_id',
          headerName: 'Shopify ID',
          width: 120,
          filter: 'agTextColumnFilter',
          hide: true, // Hidden by default but available for Advanced Filter
          valueGetter: (params) => {
            return params.data?.shopify?.product_id || params.data?.shopify_product_id || null;
          }
        },
        {
          field: 'title',
          headerName: 'Product Title',
          filter: 'agTextColumnFilter',
          width: 280,
          cellRenderer: CurrentTitleRenderer,
          editable: true,
          cellClass: 'editable-cell',
          valueGetter: (params) => {
            // Prefer enriched optimized title if available
            return params.data?.enrichment?.title?.optimized_title || params.data?.title;
          }
        },
        {
          field: 'currentDescription',
          headerName: 'Description',
          filter: true,
          width: 250,
          editable: true,
          wrapText: true,
          cellRenderer: CurrentDescriptionRenderer,
          cellClass: 'editable-cell',
          valueGetter: (params) => {
            // Prefer enriched description, then Shopify description, then legacy field
            return params.data?.enrichment?.description?.html_description
              || params.data?.body_html
              || params.data?.description
              || params.data?.currentDescription;
          }
        },
        // AI Enrichment Results Columns - Editable for review/modification
        {
          field: 'aiTitle',
          headerName: '✨ AI Title',
          width: 280,
          filter: 'agTextColumnFilter',
          editable: true,
          cellClass: 'editable-cell ai-result-cell',
          cellStyle: (params) => {
            const sku = params.data?.parentSku || params.data?.sku;
            const job = (params.context?.progressJobs || progressJobs)?.[sku];
            const hasValue = job?.results?.title?.data?.optimized_title ||
                            params.data?.enrichment?.title?.optimized_title;
            return hasValue ? { backgroundColor: 'rgba(111, 66, 193, 0.08)' } : null;
          },
          valueGetter: (params) => {
            const sku = params.data?.parentSku || params.data?.sku;
            const job = (params.context?.progressJobs || progressJobs)?.[sku];
            return job?.results?.title?.data?.optimized_title ||
                   params.data?.enrichment?.title?.optimized_title || '';
          },
          valueSetter: (params) => {
            // Store edited AI title in enrichment object
            if (!params.data.enrichment) params.data.enrichment = {};
            if (!params.data.enrichment.title) params.data.enrichment.title = {};
            params.data.enrichment.title.optimized_title = params.newValue;
            params.data.enrichment.title.edited = true;
            return true;
          }
        },
        {
          field: 'aiDescription',
          headerName: '✨ AI Description',
          width: 300,
          filter: 'agTextColumnFilter',
          editable: true,
          cellClass: 'editable-cell ai-result-cell',
          cellEditor: 'agLargeTextCellEditor',
          cellEditorParams: {
            maxLength: 5000,
            rows: 10,
            cols: 60
          },
          cellEditorPopup: true,
          cellStyle: (params) => {
            const sku = params.data?.parentSku || params.data?.sku;
            const job = (params.context?.progressJobs || progressJobs)?.[sku];
            const hasValue = job?.results?.description?.data?.html_description ||
                            params.data?.enrichment?.description?.html_description;
            return hasValue ? { backgroundColor: 'rgba(111, 66, 193, 0.08)' } : null;
          },
          valueGetter: (params) => {
            const sku = params.data?.parentSku || params.data?.sku;
            const job = (params.context?.progressJobs || progressJobs)?.[sku];
            const aiDesc = job?.results?.description?.data?.html_description ||
                          job?.results?.description?.data?.body_html ||
                          params.data?.enrichment?.description?.html_description;
            return aiDesc ? aiDesc.replace(/<[^>]*>/g, '') : '';
          },
          valueSetter: (params) => {
            // Store edited AI description in enrichment object
            if (!params.data.enrichment) params.data.enrichment = {};
            if (!params.data.enrichment.description) params.data.enrichment.description = {};
            params.data.enrichment.description.html_description = params.newValue;
            params.data.enrichment.description.edited = true;
            return true;
          }
        },
        {
          field: 'aiIngredients',
          headerName: '✨ Ingredients',
          width: 200,
          hide: true, // Hidden by default, user can enable via Columns
          filter: 'agTextColumnFilter',
          editable: true,
          cellClass: 'editable-cell ai-result-cell',
          cellStyle: (params) => {
            const sku = params.data?.parentSku || params.data?.sku;
            const job = (params.context?.progressJobs || progressJobs)?.[sku];
            const hasValue = job?.results?.metadata?.data?.ingredients ||
                            params.data?.enrichment?.metadata?.ingredients;
            return hasValue ? { backgroundColor: 'rgba(111, 66, 193, 0.08)' } : null;
          },
          valueGetter: (params) => {
            const sku = params.data?.parentSku || params.data?.sku;
            const job = (params.context?.progressJobs || progressJobs)?.[sku];
            return job?.results?.metadata?.data?.ingredients ||
                   params.data?.enrichment?.metadata?.ingredients || '';
          },
          valueSetter: (params) => {
            if (!params.data.enrichment) params.data.enrichment = {};
            if (!params.data.enrichment.metadata) params.data.enrichment.metadata = {};
            params.data.enrichment.metadata.ingredients = params.newValue;
            params.data.enrichment.metadata.edited = true;
            return true;
          }
        },
        {
          field: 'aiWeight',
          headerName: '✨ AI Weight',
          width: 120,
          filter: 'agNumberColumnFilter',
          editable: true,
          cellClass: 'editable-cell ai-result-cell',
          cellStyle: (params) => {
            const sku = params.data?.parentSku || params.data?.sku;
            const job = (params.context?.progressJobs || progressJobs)?.[sku];
            const hasValue = job?.results?.weight?.data?.estimated_weight_oz ||
                            params.data?.enrichment?.weight?.estimated_weight_oz;
            return hasValue ? { backgroundColor: 'rgba(111, 66, 193, 0.08)' } : null;
          },
          valueGetter: (params) => {
            const sku = params.data?.parentSku || params.data?.sku;
            const job = (params.context?.progressJobs || progressJobs)?.[sku];
            const unitWeightOz = job?.results?.weight?.data?.estimated_weight_oz ||
                            params.data?.enrichment?.weight?.estimated_weight_oz;
            if (!unitWeightOz) return '';
            // Multiply unit weight by productFactor for case/pack variants
            // Derive factor from SKU suffix as fallback (e.g., "430304-12" -> 12)
            const skuFactor = params.data?.sku?.match(/-(\d+)$/)?.[1];
            const productFactor = params.data?.productFactor > 1 ? params.data.productFactor : (skuFactor ? parseInt(skuFactor, 10) : 1);
            const totalWeight = (parseFloat(unitWeightOz) * productFactor).toFixed(2);
            return `${totalWeight} oz`;
          },
          valueSetter: (params) => {
            if (!params.data.enrichment) params.data.enrichment = {};
            if (!params.data.enrichment.weight) params.data.enrichment.weight = {};
            // Parse the oz value (remove "oz" suffix if present)
            // Store as unit weight (divide by productFactor if user enters total weight)
            const value = String(params.newValue).replace(/\s*oz\s*/i, '');
            const skuFactor = params.data?.sku?.match(/-(\d+)$/)?.[1];
            const productFactor = params.data?.productFactor > 1 ? params.data.productFactor : (skuFactor ? parseInt(skuFactor, 10) : 1);
            params.data.enrichment.weight.estimated_weight_oz = (parseFloat(value) / productFactor) || null;
            params.data.enrichment.weight.edited = true;
            return true;
          }
        },
        {
          field: 'aiCategory',
          headerName: '✨ AI Category',
          width: 150,
          filter: 'agTextColumnFilter',
          editable: true,
          cellClass: 'editable-cell ai-result-cell',
          cellStyle: (params) => {
            const sku = params.data?.parentSku || params.data?.sku;
            const job = (params.context?.progressJobs || progressJobs)?.[sku];
            const hasValue = job?.results?.metadata?.data?.product_type ||
                            params.data?.enrichment?.metadata?.product_type;
            return hasValue ? { backgroundColor: 'rgba(111, 66, 193, 0.08)' } : null;
          },
          valueGetter: (params) => {
            const sku = params.data?.parentSku || params.data?.sku;
            const job = (params.context?.progressJobs || progressJobs)?.[sku];
            return job?.results?.metadata?.data?.product_type ||
                   params.data?.enrichment?.metadata?.product_type || '';
          },
          valueSetter: (params) => {
            if (!params.data.enrichment) params.data.enrichment = {};
            if (!params.data.enrichment.metadata) params.data.enrichment.metadata = {};
            params.data.enrichment.metadata.product_type = params.newValue;
            params.data.enrichment.metadata.edited = true;
            return true;
          }
        },
        {
          field: 'aiAllergens',
          headerName: '✨ Allergens',
          width: 150,
          hide: true, // Hidden by default
          filter: 'agTextColumnFilter',
          editable: true,
          cellClass: 'editable-cell ai-result-cell',
          cellStyle: (params) => {
            const sku = params.data?.parentSku || params.data?.sku;
            const job = (params.context?.progressJobs || progressJobs)?.[sku];
            const allergens = job?.results?.metadata?.data?.allergens ||
                             params.data?.enrichment?.metadata?.allergens;
            return allergens?.length > 0 ? { backgroundColor: 'rgba(111, 66, 193, 0.08)' } : null;
          },
          valueGetter: (params) => {
            const sku = params.data?.parentSku || params.data?.sku;
            const job = (params.context?.progressJobs || progressJobs)?.[sku];
            const allergens = job?.results?.metadata?.data?.allergens ||
                             params.data?.enrichment?.metadata?.allergens || [];
            return Array.isArray(allergens) ? allergens.join(', ') : '';
          },
          valueSetter: (params) => {
            if (!params.data.enrichment) params.data.enrichment = {};
            if (!params.data.enrichment.metadata) params.data.enrichment.metadata = {};
            // Parse comma-separated values
            params.data.enrichment.metadata.allergens = params.newValue
              ? params.newValue.split(',').map(s => s.trim()).filter(Boolean)
              : [];
            params.data.enrichment.metadata.edited = true;
            return true;
          }
        },
        {
          field: 'productCategory',
          headerName: 'Product Category',
          width: 150,
          editable: true,
          cellRenderer: CategoryBadgeRenderer,
          cellEditor: SelectCellEditor,
          cellEditorPopup: true,
          cellEditorParams: {
            values: ['Nuts & Seeds', 'Pickled Fruits & Vegetables', 'Crackers', 'Cheese Puffs', 'Cigarettes', 'Pies & Tarts', 'Uncategorized', 'Data Analysis & Visualization']
          },
          cellClass: 'editable-cell',
          valueGetter: (params) => {
            // Priority: 1) Manual productCategory, 2) AI enrichment category
            return params.data?.productCategory ||
                   params.data?.enrichment?.metadata?.category;
          }
        },
        {
          field: 'vendor',
          headerName: 'Vendor',
          width: 130,
          editable: true,
          filter: 'agTextColumnFilter',
          cellClass: 'editable-cell',
          valueGetter: (params) => {
            // Priority: 1) Direct vendor field (from BigQuery COALESCE), 2) Enrichment vendor, 3) Brand
            return params.data?.vendor || params.data?.enrichment?.vendor || params.data?.brand;
          }
        },
        {
          field: 'productType',
          headerName: 'Product Type',
          width: 120,
          editable: true,
          cellClass: 'editable-cell',
          valueGetter: (params) => {
            // Priority: 1) AI enrichment, 2) Shopify product_type, 3) Manual productType
            return params.data?.enrichment?.metadata?.product_type ||
                   params.data?.shopify?.product_type ||
                   params.data?.productType;
          }
        },
        {
          field: 'brand',
          headerName: 'Brand',
          filter: true,
          width: 100,
          editable: true,
          cellClass: 'editable-cell',
          valueGetter: (params) => {
            // Priority: 1) Manual brand, 2) Shopify vendor, 3) AI metadata
            return params.data?.brand ||
                   params.data?.shopify?.vendor ||
                   params.data?.enrichment?.metadata?.brand;
          }
        },
        { field: 'bullets', headerName: 'Product Features', filter: true, width: 250, wrapText: true, editable: true, cellClass: 'editable-cell' },
        { field: 'amazon.asin', headerName: 'ASIN', filter: true, width: 110, cellRenderer: AsinDataRenderer },
        { field: 'productFactor', 
          headerName: 'Factor',
          editable: true, 
          filter: true,
          width: 70,
          sortable: true,
          cellClass: 'editable-cell',
          comparator: (valueA, valueB) => valueA - valueB,
          valueGetter: (params) => params.data?.productFactor ? Number(params.data?.productFactor) : 1,
          valueFormatter: params => `${params.value}`,
        },
        { field: 'searchSource', headerName: 'Source', filter: true, width: 100, hide: true },
        { field: 'cost',
          headerName: 'Cost',
          filter: true,
          sortable: true,
          width: 100,
          comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
            return valueA - valueB;
          },
          valueGetter: (params) => {
            // Priority: cost field (synced from ibmi), then ibmi_cost (flat), then ibmi.cost (nested)
            const baseCost = params.data?.cost || params.data?.ibmi_cost || params.data?.ibmi?.cost;
            if (!baseCost) return null;
            const productFactor = params.data?.productFactor || 1;
            return Number(baseCost * productFactor).toFixed(2);
          },
          valueFormatter: params => params.value ? `$${params.value}` : '-',
        },
        {
          field: 'weightOz',
          headerName: 'Weight (oz)',
          filter: true,
          width: 100,
          editable: true,
          valueGetter: (params) => {
            // Prefer enriched weight if available (AI returns estimated_weight_oz)
            const unitWeight = params.data?.enrichment?.weight?.estimated_weight_oz || params.data?.weightOz;
            if (!unitWeight) return null;
            // Derive factor from SKU suffix as fallback (e.g., "430304-12" -> 12)
            const skuFactor = params.data?.sku?.match(/-(\d+)$/)?.[1];
            const productFactor = params.data?.productFactor > 1 ? params.data.productFactor : (skuFactor ? parseInt(skuFactor, 10) : 1);
            return parseFloat(unitWeight) * productFactor;
          }
        },
        { field: 'estShippingCost', headerName: 'Shipping Cost', filter: true, width: 100, cellRenderer: ShippingDataRenderer },
        {
            field: 'pricingTemplateName',
            headerName: 'Pricing Template',
            width: 150,
            cellRenderer: (params) => {
                if (!params.data?.pricingTemplateId) {
                    return <Badge bg="secondary" text="light">No Template</Badge>;
                }
                // Show the actual template NAME (stored on product during assignment)
                return (
                    <span className="text-primary">
                        {params.data.pricingTemplateName || 'Unknown Template'}
                    </span>
                );
            },
            // Allow filtering by template status
            filter: 'agSetColumnFilter',
            filterParams: {
                values: ['assigned', 'missing'],
                valueFormatter: (params) => params.value === 'assigned' ? 'Has Template' : 'No Template'
            },
            // Custom filter value getter
            valueGetter: (params) => params.data?.pricingTemplateId ? 'assigned' : 'missing'
        },
        { field: 'shippingTemplate', headerName: 'Shipping Template', filter: true, width: 100, hide: true },
        {
            field: 'tags',
            headerName: 'Tags',
            cellRenderer: TagCellRenderer,
            width: 400,
            
        },
        { field: 'ecomm_onhand',
          filter: true,
          width: 100,
          headerName: 'On Hand',
          sortable: true,
          comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
            return valueA - valueB;
          },
          valueGetter: (params) => {
            const ecomm_onhand = params.data?.ecomm_onhand ? params.data?.ecomm_onhand : 0;
            const productFactor = params.data?.productFactor ? params.data?.productFactor : 1;
            return Math.floor(ecomm_onhand / productFactor);
          },
          valueFormatter: params => `${params.value.toFixed(0)}`,
          cellStyle: (params) => {
            const productFactor = params.data.productFactor ? params.data.productFactor : 1;
            return productFactor == 1 ? {color: 'green'} : {color: 'black'};  // Set color based on productFactor
          },
        },
        // === SHOPIFY FIELDS ===
        {
            field: 'price',
            headerName: 'Price',
            filter: true,
            editable: true,
            width: 100,
            cellClass: 'editable-cell',
            // Show calculated price if available, otherwise show existing price or dash
            valueGetter: (params) => {
                // Prefer calculated price from pricing template
                if (params.data?.calculatedPrice != null) {
                    return params.data.calculatedPrice;
                }
                // Fallback to existing price field
                return params.data?.price;
            },
            cellRenderer: (params) => {
                const price = params.value;
                const hasTemplate = !!params.data?.pricingTemplateId;
                const breakdown = params.data?.priceBreakdown;
                const templateName = params.data?.pricingTemplateName;

                // If no pricing template and no existing price, show dash
                if (!hasTemplate && (price == null || price === '')) {
                    return <span className="text-muted">-</span>;
                }
                // Show price with popover if template breakdown available
                if (price != null && price !== '' && hasTemplate && breakdown) {
                    return (
                        <PriceColumnPopover
                            price={price}
                            breakdown={breakdown}
                            templateName={templateName}
                            disabled={false}
                        />
                    );
                }
                // Show price if available (no popover if no breakdown data)
                if (price != null && price !== '') {
                    return `$${Number(price).toFixed(2)}`;
                }
                return <span className="text-muted">-</span>;
            }
        },
        {
            field: 'compareAtPrice',
            headerName: 'Compare At',
            filter: true,
            editable: true,
            width: 100,
            cellClass: 'editable-cell',
            valueGetter: params => params.data?.compareAtPrice || params.data?.mapPricing,
            valueFormatter: params => params.value ? `$${Number(params.value).toFixed(2)}` : null
        },
        {
            field: 'size',
            headerName: 'Size',
            filter: true,
            editable: true,
            width: 80,
            cellClass: 'editable-cell'
        },
        {
            field: 'barcode',
            headerName: 'Barcode',
            filter: true,
            width: 130,
            valueGetter: params => {
                const sku = params.data?.sku || '';
                const barcodes = params.data?.barcodes || {};
                // -E suffix variants: Unit UPC
                if (/-E\d*$/i.test(sku)) {
                    return barcodes.unit || '';
                }
                // Core SKU (no dash): UPC Case
                if (!sku.includes('-')) {
                    return barcodes.other || '';
                }
                // Variant with dash: only show Master Case UPC if productFactor matches masterPack
                const productFactor = Number(params.data?.productFactor) || 0;
                const masterPack = Number(params.data?.masterPack || params.data?.ibmi?.masterPack) || 0;
                if (productFactor > 0 && masterPack > 0 && productFactor === masterPack) {
                    return barcodes.stock || '';
                }
                // Manual variant (factor != masterPack): no barcode
                return '';
            }
        },
        {
            field: 'seoTitle',
            headerName: 'SEO Title',
            filter: true,
            editable: true,
            width: 200,
            cellClass: 'editable-cell',
            hide: true  // Hidden by default, user can enable
        },
        {
            field: 'descriptionHtml',
            headerName: 'Description',
            filter: true,
            editable: true,
            width: 250,
            cellClass: 'editable-cell',
            hide: true,  // Hidden by default, user can enable
            valueFormatter: params => {
                // Strip HTML tags for display
                if (!params.value) return null;
                return params.value.replace(/<[^>]*>/g, '').substring(0, 100) + '...';
            }
        },
        {
            field: 'computedStatus',
            headerName: 'Status',
            width: 140,
            editable: false,
            cellRenderer: StatusBadgeRenderer,
            filter: 'agSetColumnFilter',
            valueGetter: (params) => {
                if (!params.data) return '';
                const result = computeProductStatus(params.data);
                return result.label;
            },
        },
        {
            field: 'channels',
            headerName: 'Channels',
            width: 100,
            cellRenderer: ChannelsCellRenderer
        },
    ];
    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        filter: true,
        floatingFilter: true, // Enable floating filters for quick column filtering
        // Spreadsheet-style editing
        singleClickEdit: true,
    }), []);

    // Track selection changes - exclude group rows and update selectedData
    const onSelectionChanged = useCallback(() => {
        if (!gridRef.current?.api) return;
        const selectedNodes = gridRef.current.api.getSelectedNodes();
        // Filter out group rows - only count actual product rows
        const productNodes = selectedNodes.filter(node => !node.group && node.data);
        const count = productNodes.length;
        setSelectedCount(count);
        // Store selected data for batch operations
        setSelectedData(productNodes.map(node => node.data).filter(Boolean));
    }, []);

    // Track filter changes - update filteredCount for BatchScopePanel
    const onFilterChanged = useCallback(() => {
        if (!gridRef.current?.api) return;
        const displayedRowCount = gridRef.current.api.getDisplayedRowCount();
        setFilteredCount(displayedRowCount);
        console.log('[Grid] Filter changed - displayed rows:', displayedRowCount);
    }, []);

    // Clear selection handler
    const handleClearSelection = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.deselectAll();
            setSelectedCount(0);
            setSelectedData([]);
            setSelectAllMode(false);
        }
    }, []);

    // Ref to track expanded state for header component (avoids stale closure)
    const groupsExpandedRef = useRef(groupsExpanded);
    useEffect(() => {
        groupsExpandedRef.current = groupsExpanded;
    }, [groupsExpanded]);

    // Toggle all group expand/collapse
    const handleToggleGroups = useCallback(() => {
        if (!gridRef.current?.api) return;

        const currentState = groupsExpandedRef.current;
        const newExpandedState = !currentState;
        console.log('[Grid] Toggling all groups to:', newExpandedState ? 'expanded' : 'collapsed');

        // Use expandAll/collapseAll for more reliable toggling
        if (newExpandedState) {
            gridRef.current.api.expandAll();
        } else {
            gridRef.current.api.collapseAll();
        }

        setGroupsExpanded(newExpandedState);

        // Force header refresh to update the icon
        setTimeout(() => {
            gridRef.current?.api?.refreshHeader();
        }, 50);
    }, []);

    // Custom header component for expand/collapse all button above checkboxes
    const ExpandCollapseHeader = useCallback(() => {
        // Read from ref to always get current state
        const isExpanded = groupsExpandedRef.current;
        return (
            <div
                onClick={handleToggleGroups}
                style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    padding: '4px',
                }}
                title={isExpanded ? 'Collapse all groups' : 'Expand all groups'}
            >
                <FontAwesomeIcon
                    icon={isExpanded ? faChevronDown : faChevronRight}
                    style={{ color: '#2c7be5', fontSize: '12px' }}
                />
            </div>
        );
    }, [handleToggleGroups]);

    // Select all visible products
    const handleSelectAll = useCallback(() => {
        if (!gridRef.current?.api) return;
        gridRef.current.api.selectAll();
        setSelectAllMode(true);
    }, []);

    // Handle system tab selection (All Products tab)
    const handleSystemTabSelected = useCallback((tab) => {
        console.log('[Grid] System tab selected:', tab.id);
        if (tab.id === 'system-all-products') {
            // Set state - the separate pagination effect will handle initialization
            setIsAllProductsTab(true);
            paginationInitializedRef.current = false; // Reset to trigger initialization
            if (gridRef.current?.api) {
                gridRef.current.api.showLoadingOverlay();
            }
        }
    }, []);

    // Handle switching back to user tab
    const handleUserTabSelected = useCallback(() => {
        setIsAllProductsTab(false);
    }, []);

    // Enhanced cell value changed handler with dirty tracking
    const onCellValueChangedEnhanced = useCallback(async (params) => {
        const parentDocId = params.data.parent_docId;
        const docId = params.data.docId;
        const firestorePath = params.data._firestorePath;

        if (params.newValue !== params.oldValue) {
            // Track dirty state
            setDirtyRows(prev => {
                const newMap = new Map(prev);
                const existing = newMap.get(docId) || {};
                existing[params.colDef.field] = params.newValue;
                existing._parentDocId = parentDocId;
                existing._firestorePath = firestorePath;
                newMap.set(docId, existing);
                return newMap;
            });

            // Immediate save — use setDoc+merge so it works even if the doc doesn't exist yet
            try {
                // Use the actual Firestore path if available (avoids invalid paths from Shopify GIDs)
                const docRef = firestorePath
                    ? doc(db, firestorePath)
                    : doc(db, `productCatalog/topicz/products/${parentDocId}/variants`, docId);
                const fieldName = params.colDef.field;

                // For AI enrichment fields, write to the nested enrichment path
                let updateData;
                if (AI_FIELD_MAP[fieldName]) {
                    const paths = Object.keys(AI_FIELD_MAP[fieldName]);
                    // paths[0] is the value path, paths[1] is the edited flag
                    updateData = { [paths[0]]: params.newValue, [paths[1]]: true };
                } else {
                    updateData = { [fieldName]: params.newValue };
                }

                await setDoc(docRef, updateData, { merge: true });

                // Clear from dirty after successful save
                setDirtyRows(prev => {
                    const newMap = new Map(prev);
                    const existing = newMap.get(docId);
                    if (existing) {
                        delete existing[params.colDef.field];
                        if (Object.keys(existing).filter(k => !k.startsWith('_')).length === 0) {
                            newMap.delete(docId);
                        }
                    }
                    return newMap;
                });
            } catch (error) {
                toast.error(`Error saving: ${error.message}`, { theme: 'colored' });
            }
        }
    }, []);

    // Batch save all dirty changes
    const handleBatchSave = useCallback(async () => {
        if (dirtyRows.size === 0) return;

        setIsSaving(true);
        try {
            const batch = writeBatch(db);

            dirtyRows.forEach((changes, docId) => {
                const parentDocId = changes._parentDocId;
                const firestorePath = changes._firestorePath;
                // Use the actual Firestore path if available (avoids invalid paths from Shopify GIDs)
                const docRef = firestorePath
                    ? doc(db, firestorePath)
                    : doc(db, `productCatalog/topicz/products/${parentDocId}/variants`, docId);
                const updateData = {};
                for (const [field, value] of Object.entries(changes)) {
                    if (field.startsWith('_')) continue;
                    if (AI_FIELD_MAP[field]) {
                        const paths = Object.keys(AI_FIELD_MAP[field]);
                        updateData[paths[0]] = value;
                        updateData[paths[1]] = true;
                    } else {
                        updateData[field] = value;
                    }
                }
                batch.update(docRef, updateData);
            });

            await batch.commit();
            setDirtyRows(new Map());
            toast.success('All changes saved successfully!', { theme: 'colored' });
        } catch (error) {
            toast.error(`Error saving changes: ${error.message}`, { theme: 'colored' });
        } finally {
            setIsSaving(false);
        }
    }, [dirtyRows]);

    // Push selected products to Shopify
    const handlePushToShopify = useCallback(async () => {
        if (selectedData.length === 0) {
            toast.warning('Please select products to push', { theme: 'colored' });
            return;
        }

        setIsPushing(true);
        try {
            // Prepare products for push - group by parent SKU for variant handling
            // Prefer AI-enriched title/description over IBMi originals
            const productsToPush = selectedData.map(product => {
                const sku = product.sku || '';
                const barcodes = product.barcodes || {};
                let barcode;
                if (/-E\d*$/i.test(sku)) {
                    barcode = barcodes.unit || undefined;
                } else if (!sku.includes('-')) {
                    barcode = barcodes.other || undefined;
                } else {
                    // Variant: only send Master Case UPC if productFactor matches masterPack
                    const pf = Number(product.productFactor) || 0;
                    const mp = Number(product.masterPack || product.ibmi?.masterPack) || 0;
                    barcode = (pf > 0 && mp > 0 && pf === mp) ? (barcodes.stock || undefined) : undefined;
                }
                return {
                    sku,
                    parentSku: product.parentSku,
                    title: product.enrichment?.title?.optimized_title || product.title,
                    descriptionHtml: product.enrichment?.description?.html_description || product.descriptionHtml || product.enrichment?.description_html,
                    seoTitle: product.enrichment?.seo?.seo_title || product.seoTitle || product.enrichment?.seo_title,
                    price: product.calculatedPrice || product.price,
                    compareAtPrice: product.compareAtPrice || product.mapPricing,
                    weight: (() => {
                        const unitWeight = product.enrichment?.weight?.estimated_weight_oz || product.weight || product.weightOz;
                        if (!unitWeight) return undefined;
                        // Multiply unit weight by productFactor for case/pack variants
                        const skuFactor = product.sku?.match(/-(\d+)$/)?.[1];
                        const productFactor = product.productFactor > 1 ? product.productFactor : (skuFactor ? parseInt(skuFactor, 10) : 1);
                        return parseFloat(unitWeight) * productFactor;
                    })(),
                    vendor: product.vendor,
                    productType: product.category || product.productType,
                    tags: product.tags || product.enrichment?.tags,
                    barcode,
                    size: product.size,
                    image: product.thumbnail || product.primaryImage,
                    shopify_product_id: product.shopify_product_id,
                    shopify_handle: product.shopify_handle,
                    _firestorePath: product._firestorePath
                };
            });

            // Call Cloud Function to push to Shopify
            const response = await fetch(`${CLOUD_FUNCTIONS_URL}/shopifyBatchPush`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ products: productsToPush })
            });

            const result = await response.json();

            if (result.success) {
                const { created, updated, variantsAdded, failed } = result;
                const successCount = (created || 0) + (updated || 0) + (variantsAdded || 0);

                // Build detailed message
                const parts = [];
                if (created > 0) parts.push(`${created} created`);
                if (variantsAdded > 0) parts.push(`${variantsAdded} variants added`);
                if (updated > 0) parts.push(`${updated} updated`);

                if (failed > 0) {
                    toast.warning(`Pushed ${successCount} products (${parts.join(', ')}). ${failed} failed.`, { theme: 'colored' });
                } else {
                    toast.success(`Successfully pushed to Shopify: ${parts.join(', ')}!`, { theme: 'colored' });
                }

                // Refresh the grid to show updated Shopify status
                if (typeof refreshProductSearch === 'function') {
                    refreshProductSearch();
                }
            } else {
                // Build error message from errors array if available
                let errorMsg = 'Push failed';
                if (result.errors && result.errors.length > 0) {
                    errorMsg = result.errors.map(e => `${e.sku}: ${e.error}`).join('; ');
                } else if (result.error) {
                    errorMsg = result.error;
                }
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error('Push to Shopify error:', error);
            toast.error(`Error pushing to Shopify: ${error.message}`, { theme: 'colored' });
        } finally {
            setIsPushing(false);
        }
    }, [selectedData, refreshProductSearch]);

    // Row class rules for enrichment failed highlighting
    const rowClassRules = useMemo(() => ({
        'enrichment-failed': (params) => {
            if (!params.data?.sku) return false;
            return failedSkus.includes(params.data.sku) || failedSkus.includes(params.data.parentSku);
        }
    }), [failedSkus]);

    /** Grid Options */
    const gridOptions = {
        animateRows: true,
        columnDefs: basicColumnDefs,
        defaultColDef: defaultColDef,
        components: {
            expandCollapseHeader: ExpandCollapseHeader,
        },
        domLayout: 'normal',
        getRowId: params => params.data?.docId,
        getRowHeight: (params) => {
            if (params.node.group) return 32;
            return 28; // Compact row height - shows ~25+ rows in viewport
        },
        // Spreadsheet navigation
        enterNavigatesVerticallyAfterEdit: true,
        tabToNextCell: true,
        stopEditingWhenCellsLoseFocus: true,
        // Grouping - default to expanded
        groupDefaultExpanded: 1,
        groupUseEntireRow: true,
        groupDisplayType: 'groupRows',
        groupRowRenderer: ProductGroupCellRenderer,
        groupRowRendererParams: { someProp: 'someValue' },
        // Event handlers
        onCellValueChanged: onCellValueChangedEnhanced,
        onFirstDataRendered: onGridReady,
        onColumnMoved: onColumnChanged,
        onColumnVisible: onColumnChanged,
        onColumnResized: onColumnChanged,
        onColumnPinned: onColumnChanged,
        onSelectionChanged: onSelectionChanged,
        // UI
        loadingOverlayComponent: LoadingAnimationComponent,
        overlayNoRowsTemplate: '<span class="ag-overlay-loading-center">Search For Products Above</span>',
        rowSelection: "multiple",
        suppressRowClickSelection: true, // Only select via checkbox
        rowMultiSelectWithClick: false,
        // Clipboard
        enableRangeSelection: true,
        suppressCopyRowsToClipboard: false,
        // Sidebar - keeping minimal for cleaner UI
        sideBar: false, // Disabled - using custom column toggle
        suppressModelUpdateAfterUpdateTransaction: true,
        // Advanced Filter
        enableAdvancedFilter: true,
        advancedFilterBuilderParams: {
            showMoveButtons: true
        },
        onFilterChanged: onFilterChanged,
        // Failed enrichment row highlighting
        rowClassRules: rowClassRules,
    };

    // Menu Bar Action Handlers
    async function handleAction(actionType, payload) {
        const selectedNodes = gridRef.current.api.getSelectedNodes();
        const selectedDataRaw = selectedNodes.map(node => node.data);
        // selectedDataRaw will include the Group Row nodes as well which will be undefined
        const selectedData = selectedDataRaw.filter(Boolean); // remove group rows
        setSelectedData(selectedData);

        // setConfirmationModalShow(true);
                
        switch (actionType) {

            // PRODUCTS FILE MENU ACTIONS
            case 'NEW_WORKSPACE':
                // opens a new tab
                const newTabRef = await tabController.createTab({name: 'New Tab', type: 'workspace', data: selectedData});
                // setActiveTab(newTabRef.id);
                tabController.assignActiveTab(newTabRef.id);
                break;
            case 'OPEN_WORKSPACE':
                // opens the workspace manager modal to the browse tab
                setShowOpenWorkspaceModal(true);
                break;
            case 'MANAGE_WORKSPACES':
                // opens the workspace manager modal to the manage tab
                break;
            case 'TAGS':
                // setSelectedData(selectedData);
                setTagsModalShow(true);
                break;
            case 'MERLIN_AI':
                // opens the workspace manager modal
                break;
            case 'ADD_PRODUCTS_TO_WORKSPACE':
                // adds the currently visible products to the workspace permanently
                setConfirmationModalTitle('Add Products to Workspace');
                setConfirmationModalMessage(`Are you sure you want to add all products to the workspace?`);
                confirmPinAllProducts(searchResults, tabController);
                break;

            // EDIT FILE MENU ACTIONS
            case 'COPY':
                // copies the selected data to the clipboard
                copyToClipboard();
                break;
            case 'PASTE':
                // pastes the data from the clipboard into the workspace
                pasteFromClipboard();
                break;

            
            // PRICING FILE MENU ACTIONS
            case 'ASSIGN__PRICING_TEMPLATE':
                // opens the assign pricing template modal
                setAssignmentType('pricing');
                // setSelectedData(selectedData);
                setAssignTemplateModalShow(true);
                break;
            case 'MANAGE_PRICING_TEMPLATES':
                // opens the manage pricing template modal
                setShowManagePricingTemplatesModal(true);
                break;
            case 'REPRICE':
                setConfirmationModalTitle('Reprice Products');
                setConfirmationModalMessage(`Are you sure you want to create ${selectedData.length} repricer jobs for the selected products?`);
                confirmRepricerJobs(user.uid, selectedData);
                break;

            // SHIPPING FILE MENU ACTIONS
            case 'ESTIMATE_SHIPPING':
                setConfirmationModalTitle('Create Shipping Estimate');
                setConfirmationModalMessage(`Are you sure you want to create ${selectedData.length} shipping estimate jobs for the selected products?`);
                confirmShippingEstimate();
                break;
            case 'ASSIGN_SHIPPING_TEMPLATE':
                // opens the assign pricing template modal
                setAssignmentType('shipping');
                setAssignTemplateModalShow(true);
                break;
            case 'MANAGE_SHIPPING_TEMPLATES':
                // opens the manage pricing template modal
                setShowManageShippingTemplatesModal(true);
                break;

            // LISTINGS FILE MENU ACTIONS
            case 'ASSIGN_LISTING_TEMPLATE':
                // opens the assign pricing template modal
                break;
            case 'MANAGE_LISTING_TEMPLATES':
                // opens the manage pricing template modal
                break;
            case 'LISTING_READINESS_CHECK':
                setShowListingReadinessModal(true);
                break;
            
            // INTEL File   MENU ACTIONS
            case 'DISCOVER_ASINS':
                // setSelectedData(selectedData);
                // createAsinDiscoveryJobs(selectedData)
                setConfirmationModalTitle('ASIN Discovery');
                setConfirmationModalMessage(`Are you sure you want to create ${selectedData.length} ASIN Discovery jobs for the selected products?`);
                confirmAsinDiscovery();
                break;
            case 'ESTIMATE_AMAZON_FEES':
                setConfirmationModalTitle('Estimate Amazon Fees');
                setConfirmationModalMessage(`Are you sure you want to create ${selectedData.length} Amazon Fee Estimate jobs for the selected products?`);
                confirmAmazonFeeEstimate();
                console.log("CLICK")
                break;

            // AI ENRICHMENT
            case 'RUN_AI_ENRICHMENT':
                if (selectAllMode && selectedData.length > 0) {
                    // Show select all action modal first
                    setPendingAction({ type: 'RUN_AI_ENRICHMENT', data: selectedData });
                    setShowSelectAllActionModal(true);
                } else {
                    setShowEnrichmentModal(true);
                }
                break;

            // IMAGE SOURCING
            case 'SOURCE_IMAGES':
                if (selectedData.length === 0) {
                    toast.warning('Please select products to source images', { theme: 'colored' });
                } else {
                    setShowImageSourcingPanel(true);
                }
                break;

            default:
                console.log('Error no action type found');
                break;
        }
        gridRef.current.api.refreshCells();
    }

    /** Menu Action Handlers */
    const handleConfirmationModalConfirm = async (data) => {
        if (confirmationModalAction) {
            confirmationModalAction(data);
        }
    }
    const execActionWithConfirmation = (action) => {
        setConfirmationModalAction(() => action);
        setConfirmationModalShow(true);
    };
    const confirmAsinDiscovery = () => execActionWithConfirmation(createAsinDiscoveryJobs);
    const confirmShippingEstimate = () => execActionWithConfirmation(createShippingEstimateJobs);
    const confirmRepricerJobs = (uid, selectedData) => execActionWithConfirmation(() => createActivePricerJobs(uid, selectedData));
    const confirmAmazonFeeEstimate = () => execActionWithConfirmation(createAmazonFeeDiscoveryJobs);
    const confirmPinAllProducts = (skus, controller) => execActionWithConfirmation(() => pinAllProducts(skus, controller));

    // AI Enrichment handlers
    const handleEnrichmentConfirm = async (selectedModes) => {
        setEnrichmentLoading(true);
        try {
            // Build unique SKU list with product context (title, vendor, images)
            // so the enrichment worker doesn't depend solely on Firestore lookups
            const skuContextMap = {};
            selectedData.forEach(d => {
                const sku = d.parentSku || d.sku;
                if (!sku || skuContextMap[sku]) return;
                skuContextMap[sku] = {
                    title: d.title || d.shopify_title || d.name || '',
                    vendor: d.vendor || '',
                    image: d.thumbnail || d.primaryImage || '',
                };
            });
            const uniqueSkus = Object.keys(skuContextMap);

            if (uniqueSkus.length === 0) {
                throw new Error('No valid SKUs selected');
            }

            // Call enrichment trigger cloud function
            const response = await fetch(`${CLOUD_FUNCTIONS_URL}/enrichmentTrigger`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    skus: uniqueSkus,
                    options: {
                        modes: selectedModes,
                        force: false,
                        config: { bypassSeoProtection: true }
                    },
                    context: skuContextMap
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to trigger enrichment');
            }

            // Create cancel function for context
            const cancelFn = async () => {
                // Get pending job IDs from progressJobs at time of cancel
                const pendingJobIds = Object.values(progressJobs)
                    .filter(j => ['pending', 'processing'].includes(j.status))
                    .map(j => j.id);
                if (pendingJobIds.length > 0) {
                    await cancelJobs(pendingJobIds);
                }
            };

            // Start context tracking (for global indicator and progress panel)
            startEnrichment(uniqueSkus, cancelFn);

            // Start listening for real-time job progress
            startListening(uniqueSkus);

            const skippedCount = result.skipped || 0;
            if (result.queued > 0) {
                toast.success(`Started enrichment for ${result.queued} products (${selectedModes.join(', ')})${skippedCount > 0 ? ` — ${skippedCount} skipped (already processing)` : ''}`, { theme: 'colored' });
            } else {
                toast.warning(`All ${uniqueSkus.length} products skipped — enrichment already in progress`, { theme: 'colored' });
            }
            setShowEnrichmentModal(false);
        } catch (error) {
            toast.error(`Enrichment error: ${error.message}`, { theme: 'colored' });
        } finally {
            setEnrichmentLoading(false);
        }
    };

    const handleSelectAllActionSelectedOnly = () => {
        setShowSelectAllActionModal(false);
        // Proceed with selected data only
        if (pendingAction?.type === 'RUN_AI_ENRICHMENT') {
            setShowEnrichmentModal(true);
        }
    };

    const handleSelectAllActionAllProducts = async () => {
        setShowSelectAllActionModal(false);
        // TODO: Implement batch operation for all products
        // For now, show the enrichment modal but warn about limitations
        toast.warning('Applying to all products - this will process in batches', { theme: 'colored' });
        if (pendingAction?.type === 'RUN_AI_ENRICHMENT') {
            setShowEnrichmentModal(true);
        }
    };

    // Open enrichment modal from header button
    const handleOpenEnrichmentModal = () => {
        if (selectedData.length === 0) {
            toast.warning('Please select products to enrich', { theme: 'colored' });
            return;
        }
        setShowEnrichmentModal(true);
    };

    // Approve enrichment for selected products
    const handleApproveEnrichment = async () => {
        // Get selected products that have completed enrichment jobs
        const productsToApprove = selectedData.filter(product => {
            const job = progressJobs[product.parentSku || product.sku];
            return job && (job.status === 'completed' || job.status === 'partial');
        });

        if (productsToApprove.length === 0) {
            toast.warning('No enriched products selected to approve', { theme: 'colored' });
            return;
        }

        setIsApproving(true);
        let approvedCount = 0;
        let errorCount = 0;

        try {
            for (const product of productsToApprove) {
                const job = progressJobs[product.parentSku || product.sku];
                if (!job?.id) continue;

                try {
                    const response = await fetch(`${CLOUD_FUNCTIONS_URL}/enrichmentApprove`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ jobId: job.id })
                    });

                    if (response.ok) {
                        approvedCount++;
                    } else {
                        console.error(`Failed to approve job ${job.id}`);
                        errorCount++;
                    }
                } catch (err) {
                    console.error(`Error approving job ${job.id}:`, err);
                    errorCount++;
                }
            }

            if (approvedCount > 0) {
                toast.success(`Approved ${approvedCount} enrichment${approvedCount > 1 ? 's' : ''}`, { theme: 'colored' });
            }
            if (errorCount > 0) {
                toast.error(`Failed to approve ${errorCount} enrichment${errorCount > 1 ? 's' : ''}`, { theme: 'colored' });
            }

            // Refresh the data to show updated statuses
            if (refreshProductSearch) {
                refreshProductSearch();
            }
        } catch (error) {
            toast.error(`Approval error: ${error.message}`, { theme: 'colored' });
        } finally {
            setIsApproving(false);
        }
    };

    // Calculate count of selected products with completed enrichment (for approve button)
    const enrichedSelectedCount = selectedData.filter(product => {
        const job = progressJobs[product.parentSku || product.sku];
        return job && (job.status === 'completed' || job.status === 'partial');
    }).length;


    const copyToClipboard = () => {
        const selectedNodes = gridRef.current.api.getSelectedNodes();
        const selectedDataRaw = selectedNodes.map(node => node.data);
        // selectedDataRaw will include the Group Row nodes as well which will be undefined
        const selectedData = selectedDataRaw.filter(Boolean); // remove group rows
        const selectedSkus = selectedData.map(data => data.sku);
        saveToClipboard({skus: selectedSkus, data: selectedData});
    }

    const pasteFromClipboard = () => {
        const pastedData = clipboard?.data;
        if (!pastedData || !Array.isArray(pastedData) || pastedData.length === 0) {
            console.log('[Grid] No clipboard data to paste');
            return;
        }
        const parentSkus = pastedData.map(data => data.parentSku);
        const uniqueSkus = [...new Set(parentSkus)];
        const complexSkuList = uniqueSkus.map(sku => {
            return {
                sku: sku,
                score: 103,
                searchSource: 'clipboard',
            }
        });
        tabController.addProduct(tabController.activeTab, complexSkuList);
        tabController.assignActiveTab(tabController.activeTab);
    }

    const nextTab = () => {

        // find the index of the activeTab
        const index = visibleTabs.findIndex(tab => tab.id === tabController.activeTab);
        if (gridRef.current) {
            gridRef.current.api.showLoadingOverlay();
            if (index === -1) {
                tabController.assignActiveTab(visibleTabs[0]?.id);
            } else if (index === visibleTabs.length - 1) {
                tabController.assignActiveTab(visibleTabs[1]?.id);
            } else {
                tabController.assignActiveTab(visibleTabs[index + 1]?.id);
            }
            gridRef.current.api.setRowData([]);
            setSkuList(visibleTabs[index]?.skus);
            gridRef.current.api.hideOverlay();
        }
        
    }

    const previousTab = () => {
        // find the index of the activeTab
        const index = visibleTabs.findIndex(tab => tab.id === tabController.activeTab);
        if (gridRef.current) {
            gridRef.current.api.showLoadingOverlay();
            if (index === -1) {
                tabController.assignActiveTab(visibleTabs[1]?.id);
            } else if (index === 0) {
                tabController.assignActiveTab(visibleTabs[visibleTabs.length - 1]?.id);
            } else {
                tabController.assignActiveTab(visibleTabs[index - 1]?.id);
            }
            gridRef.current.api.setRowData([]);
            setSkuList(visibleTabs[index]?.skus);
            gridRef.current.api.hideOverlay();
        }
    }

    useKeystroke({
        'Cmd+c': () => copyToClipboard(),
        'Ctrl+c': () => copyToClipboard(),
        'Cmd+v': () => pasteFromClipboard(),
        'Ctrl+v': () => pasteFromClipboard(),
        'Cmd+ArrowRight': () => nextTab(),
        'Ctrl+ArrowRight': () => nextTab(),
        'Cmd+ArrowLeft': () => previousTab(),
        'Ctrl+ArrowLeft': () => previousTab(),
    });

    return (
        !authLoading &&
        <ActionsContext.Provider value={{handleAction}}>
            <Card>
                {/* New Header Bar with Back, Edit Count, Columns, Save */}
                <HeaderBar
                    gridRef={gridRef}
                    productCount={productSearchProducts.length || allResults.length}
                    selectedCount={selectedCount}
                    dirtyCount={dirtyRows.size}
                    onSave={handleBatchSave}
                    isSaving={isSaving}
                    totalProducts={totalProducts}
                    groupsExpanded={groupsExpanded}
                    onToggleGroups={handleToggleGroups}
                    selectAllMode={selectAllMode}
                    onSelectAll={handleSelectAll}
                    onClearSelection={handleClearSelection}
                    onPushToShopify={handlePushToShopify}
                    isPushing={isPushing}
                    // AI Enrichment props
                    onEnrich={handleOpenEnrichmentModal}
                    isEnriching={enrichmentLoading}
                    onApproveEnrichment={handleApproveEnrichment}
                    isApproving={isApproving}
                    enrichedCount={enrichedSelectedCount}
                >
                    <MenuBar gridRef={gridRef} />
                </HeaderBar>

                {/* Enrichment Progress Panel - shows when enrichment is running or complete */}
                <EnrichmentProgressPanel />

                {/* Search Bar Row */}
                <div className='px-3 py-2 bg-light border-bottom'>
                    <ProductSearchBar
                        onSearchChange={handleSearchChange}
                        isLoading={productSearchLoading}
                        value={searchQuery}
                        gridRef={gridRef}
                    />
                </div>

                {/* Filter Preset Bar - Quick access to common filters via server-side filtering */}
                <FilterPresetBar
                    gridRef={gridRef}
                    activePreset={activeFilterPreset}
                    onPresetChange={setActiveFilterPreset}
                    onFilterChange={handleFilterChange}
                    activeSort={activeSort}
                    onSortChange={handleSortChange}
                />

                {/* Batch Scope Panel - shows selection summary with estimates */}
                <BatchScopePanel
                    selectedCount={selectedCount}
                    totalProducts={totalProducts || 0}
                    filteredCount={filteredCount}
                    onClearSelection={handleClearSelection}
                />

                {/* Bulk Action Toolbar - appears when rows selected */}
                <BulkActionToolbar
                    selectedCount={selectedCount}
                    onClearSelection={handleClearSelection}
                    gridRef={gridRef}
                />
                
                {/* Tab Container */}
                <Row>
                    <Col>
                        <TabContainer
                            tabs={tabs}
                            loading={tabsLoading}
                            tabController={tabController}
                            uid={user.uid}
                            gridRef={gridRef}
                            onSystemTabSelected={handleSystemTabSelected}
                        />
                    </Col>
                </Row>
                
                {/* AG Grid */}
                <div
                    className="ag-theme-alpine"
                    style={{ width: '100%', height: '68vh', maxHeight: '78vh' }}
                >
                    <AgGridReact
                        className="border-0"
                        style={{zIndex: 99999}}
                        ref={gridRef}
                        {...gridOptions}
                        context={{ progressJobs }}
                    />
                </div>

                {/* Server-side Pagination Controls - Always visible, powered by productSearch */}
                <ServerPaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalProducts={totalProducts}
                    onPrevPage={goToPrevPage}
                    onNextPage={goToNextPage}
                    onGoToPage={goToPage}
                    loading={productSearchLoading}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                />
            </Card>
            <BulkTagsModal
                show={tagsModalShow}
                setShow={setTagsModalShow}
                data={selectedData}
                gridRef={gridRef}
            />
            <ConfirmationModal
                show={confirmationModalShow}
                title={confirmationModalTitle}
                message={confirmationModalMessage}
                data={selectedData}
                onConfirm={handleConfirmationModalConfirm}
                onClose={() => setConfirmationModalShow(false)}
            />
            <AssignTemplateModal
                type={assignmentType}
                show={assignTemplateModalShow}
                setShow={setAssignTemplateModalShow}
                data={selectedData}
                gridRef={gridRef}
                setRefetch={setManualRefetch}
            />
            <AsinDetailModal
                show={asinDetailModalShow}
                onClose={() => setAsinDetailModalShow(false)}
                data={selectedVariantData}
            />
            <ManagePricingTemplatesModal
                show={showManagePricingTemplatesModal}
                onClose={() => setShowManagePricingTemplatesModal(false)}
            />
            <ManageShippingTemplatesModal
                show={showManageShippingTemplatesModal}
                onClose={() => setShowManageShippingTemplatesModal(false)}
            />
            <OpenWorkspaceModal
                show={showOpenWorkspaceModal}
                onClose={() => setShowOpenWorkspaceModal(false)}
                tabController={tabController}
            />
            <ListingReadinessOffcanvas
                show={showListingReadinessModal}
                onHide={() => setShowListingReadinessModal(false)}
                gridRef={gridRef}
                data={allResults}
            />
            <EnrichmentModeModal
                show={showEnrichmentModal}
                onClose={() => setShowEnrichmentModal(false)}
                onConfirm={handleEnrichmentConfirm}
                selectedCount={selectedData.length}
                selectedProducts={selectedData}
                progressJobs={progressJobs}
                onCalculateSkipCounts={getSkipCounts}
                isLoading={enrichmentLoading}
            />
            <SelectAllActionModal
                show={showSelectAllActionModal}
                onClose={() => setShowSelectAllActionModal(false)}
                selectedCount={selectedCount}
                totalProducts={totalProducts}
                actionName="AI Enrichment"
                onSelectedOnly={handleSelectAllActionSelectedOnly}
                onAllProducts={handleSelectAllActionAllProducts}
            />
            <ImageSourcingPanel
                show={showImageSourcingPanel}
                onClose={() => setShowImageSourcingPanel(false)}
                selectedSkus={selectedData.map(d => d.parentSku || d.sku).filter(Boolean)}
                selectedProducts={selectedData}
            />

        </ActionsContext.Provider>
    )
}

function chunkArray(array, size) {
    const results = [];
    for (let i = 0; i < array.length; i += size) {
        results.push(array.slice(i, i + size));
    }
    return results;
}

function applyHeaderStyle(){
    const style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = `
        .ag-header-cell-label {
            padding: 1px 1px; /* Reduce padding */
            font-size: 11px; /* Smaller font size */
        }
        .ag-header-container {
            min-height: 15px; /* Reduce the height of the header row */
        }
        .ag-header-cell {
            line-height: 10px; /* Setting the line height to the desired height */
            height: 10px; /* Setting the height of the cell */
        }
        .ag-header-cell-label .ag-header-cell-text {
            line-height: normal; /* Adjust line-height as needed */
        }
        .ag-header-cell-label .ag-text-field-input,
        .ag-header-cell-label .ag-header-icon {
            margin: 0; /* Remove margin */
        }
        `;
    document.head.appendChild(style);
}

function applyRowStyle() {
    const style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = `
        /* Tighten up the cell padding */
        .ag-cell {
            padding: 2px 4px; /* Adjust padding to your preference */
            font-size: 12px; /* Smaller font size for content */
            line-height: 1; /* Tighten line height */
        }

        /* Adjust the height of the rows */
        .ag-row {
            height: 25px; /* Adjust the height to your preference */
        }

        /* Adjust header styles if needed */
        .ag-header-cell-label {
            padding: 1px 1px; /* Reduce padding */
            font-size: 10px; /* Smaller font size */
        }

        /* Style for pinned rows if necessary */
        .ag-pinned-left-cols-container .ag-cell,
        .ag-pinned-right-cols-container .ag-cell {
            padding: 2px 4px; /* Ensure consistent padding with other cells */
        }
    `;
    document.head.appendChild(style);
}
